import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "./api";
import BirthLocationMapField from "./BirthLocationMapField";
import {
  CurrencyAmountInput,
  normalizeOccupationCurrencyCode,
} from "./currencyOptions";
import HeightInputField from "./HeightInputField";
import SearchableSelect from "./SearchableSelect";
import {
  getContactValueFieldLabel,
  getIndianPhoneInputValue,
  IndianPhoneInput,
  isIndianPhoneContactType,
  normalizeIndianPhone,
  toStoredIndianPhoneValue,
  validateIndianPhoneValue,
} from "./indiaPhone";
import SakhaAssignmentsField from "./SakhaAssignmentsField";
import TimeInput12Hour from "./TimeInput12Hour";
import { uploadProfileDocuments } from "./uploadDocuments";
import {
  MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_LABEL,
  prepareFilesForUpload,
} from "./uploadFileCompression";

const STEPS = [
  { id: "duplicate", label: "Duplicate Check", navLabel: "Duplicate" },
  { id: "basic", label: "Profile Basics", navLabel: "Basics" },
  { id: "address_contacts", label: "Address & Contacts", navLabel: "Contacts" },
  { id: "personal", label: "Personal Details", navLabel: "Personal" },
  { id: "career", label: "Education & Career", navLabel: "Career" },
  { id: "family", label: "Family", navLabel: "Family" },
  { id: "relatives", label: "Relatives", navLabel: "Relatives" },
  { id: "preferences", label: "Preferences" },
  { id: "media", label: "Tags & Media", navLabel: "Media" },
];

const DUPLICATE_STEP_INDEX = 0;
const BASIC_STEP_INDEX = 1;
const ADDRESS_CONTACTS_STEP_INDEX = 2;
const FAMILY_STEP_INDEX = 5;
const RELATIVES_STEP_INDEX = 6;

const FAMILY_RELATION_TYPES = new Set(["mother", "father", "sister", "brother"]);

const emptyEducation = {
  education_category_id: "",
  degree_name: "",
  education_description: "",
  institution_name: "",
  education_country_id: "",
  education_state_id: "",
  education_district_id: "",
  education_tehsil_id: "",
  education_city_id: "",
  education_location: "",
  passing_year: "",
  education_status: "completed",
  is_highest_degree: false,
  sort_order: "",
};

const emptyRelativeEducation = {
  education_category_id: "",
  degree_name: "",
  education_description: "",
  institution_name: "",
  passing_year: "",
  is_highest_degree: true,
  sort_order: 1,
};

const emptyRelative = {
  relation_type_id: "",
  relation_type: "",
  family_side: "none",
  first_name: "",
  middle_name: "",
  last_name: "",
  full_name: "",
  gender: "",
  phone: "",
  email: "",
  contacts: [],
  country: "India",
  state_id: "",
  district_id: "",
  tehsil_id: "",
  city_id: "",
  sakha_id: "",
  marital_status: "",
  is_future_prospect: false,
  future_prospect_birth_date: "",
  future_prospect_eligible_year: "",
  is_deceased: false,
  occupation_type_id: "",
  occupation_type: "",
  occupation_details: "",
  company_name: "",
  work_profile: "",
  status: "active",
  sort_order: "",
  education: [{ ...emptyRelativeEducation }],
};

const emptyOccupation = {
  occupation_type_id: "",
  occupation_type: "",
  company_name: "",
  occupation_details: "",
  work_profile: "",
  annual_income_amount: "",
  annual_income_currency_code: "INR",
  annual_turnover_amount: "",
  annual_turnover_currency_code: "INR",
  is_primary: false,
  sort_order: "",
};

const emptyContact = {
  contact_type: "mobile",
  contact_value: "",
  normalized_value: "",
  contact_person_name: "",
  contact_person_relation: "self",
  is_primary: false,
  is_verified: false,
};

const emptyAddress = {
  address_type: "current",
  country_id: "",
  address_line1: "",
  address_line2: "",
  state_id: "",
  district_id: "",
  tehsil_id: "",
  city_id: "",
  village_id: "",
  visa_status_id: "",
  visa_name_id: "",
  pincode: "",
};

const defaultForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  gender_id: "",
  gender: "",
  birth_date: "",
  dob: "",
  birth_time: "",

  birth_country: "India",
  birth_state_id: "",
  birth_district_id: "",
  birth_tehsil_id: "",
  birth_place_city_id: "",
  birth_place_id: "",
  birth_place: "",
  birth_latitude: "",
  birth_longitude: "",

  country: "India",
  state_id: "",
  district_id: "",
  tehsil_id: "",
  city_id: "",
  current_location_id: "",

  mobile: "",
  email: "",
  referral_source_id: "",
  contacts: [],

  marital_status_id: "",
  marital_status: "",
  marital_status_description: "Never married",
  has_children: false,
  marital_field_details: "",
  height_cm: "",
  height: "",
  complexion_id: "",
  complexion: "",
  blood_group_id: "",
  blood_group: "",
  manglik: "Do Not Know",
  sampraday_id: "",
  sampraday_details: "",
  has_disability: false,
  disability_description: "",
  about_me: "",

  caste_id: "",
  sakha_id: "",
  dadisa_sakha_id: "",
  nanisa_sakha_id: "",
  mamasa_sakha_id: "",
  village_id: "",

  educations: [{ ...emptyEducation, is_highest_degree: true, sort_order: 1 }],
  occupations: [{ ...emptyOccupation, is_primary: true, sort_order: 1 }],

  relatives: [
    { ...emptyRelative, relation_type: "father", gender: "Male", family_side: "none", sort_order: 1 },
    { ...emptyRelative, relation_type: "mother", gender: "Female", family_side: "none", sort_order: 2 },
  ],

  addresses: [{ ...emptyAddress }],

  preferences: {
    preferred_age_min: "",
    preferred_age_max: "",
    preferred_age_min_unit: "months",
    preferred_age_max_unit: "months",
    preferred_age_gap_months: "",
    preferred_caste_ids: [],
    non_preferred_sakha_ids: [],
    preferred_city_ids: [],
    non_preferred_city_ids: [],
    preferred_city_tier_ids: [],
    preferred_education_category_ids: [],
    non_preferred_education_category_ids: [],
    preferred_degrees: [],
    non_preferred_degrees: [],
    preferred_occupation_types: [],
    non_preferred_occupation_types: [],
    notes: "",
  },
  tags: [],
};

export default function CreateProfile({
  token,
  user,
  onDone,
  onViewProfile,
  freshStartKey = 0,
  workflowContext = null,
}) {
  const workflowItemId =
    workflowContext?.type === "biodata" && workflowContext?.itemId
      ? String(workflowContext.itemId).trim()
      : "";
  const isBiodataWorkflow = Boolean(workflowItemId);
  const draftStorageKey = useMemo(
    () => getCreateProfileDraftKey(user, isBiodataWorkflow ? `biodata:${workflowItemId}` : ""),
    [user, isBiodataWorkflow, workflowItemId]
  );
  const initialDraft = useMemo(
    () => (freshStartKey ? null : readCreateProfileDraft(draftStorageKey)),
    [draftStorageKey, freshStartKey]
  );
  const [step, setStep] = useState(() => normalizeCreateProfileStep(initialDraft?.step));
  const [form, setForm] = useState(() => mergeCreateProfileDraftForm(initialDraft?.form));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [lookups, setLookups] = useState({
    genders: [],
    locations: [],
    cityTiers: [],
    maritalStatus: [],
    complexions: [],
    bloodGroups: [],
    occupationTypes: [],
    relationTypes: [],
    tags: [],
    castes: [],
    sakhas: [],
    sampradays: [],
    educationCategories: [],
    degreeNames: [],
    documentTypes: [],
    referralSources: [],
    visaStatuses: [],
    visaNames: [],
  });
  const visibleGenders = useMemo(
    () =>
      safeArray(lookups.genders).filter(
        (g) => String(g?.name || g?.display_label || "").trim().toLowerCase() !== "other"
      ),
    [lookups.genders]
  );

  const [duplicateChecked, setDuplicateChecked] = useState(Boolean(initialDraft?.duplicateChecked));
  const [duplicateChecking, setDuplicateChecking] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState(() => safeArray(initialDraft?.duplicateMatches));
  const [duplicateAction, setDuplicateAction] = useState(initialDraft?.duplicateAction || "");
  const [duplicateTargetId, setDuplicateTargetId] = useState(initialDraft?.duplicateTargetId || "");
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(Boolean(initialDraft?.duplicateConfirmed));
  const [duplicateResolutionMode, setDuplicateResolutionMode] = useState(
    initialDraft?.duplicateResolutionMode === "later" ? "now" : (initialDraft?.duplicateResolutionMode || "")
  );

  const [imageFiles, setImageFiles] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [documentTypeId, setDocumentTypeId] = useState("");
  const [preparingPhotoFiles, setPreparingPhotoFiles] = useState(false);
  const [photoPreparationStatus, setPhotoPreparationStatus] = useState("");
  const [preparingDocumentFiles, setPreparingDocumentFiles] = useState(false);
  const [documentPreparationStatus, setDocumentPreparationStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");
  const [documentUploadSummary, setDocumentUploadSummary] = useState("");
  const [success, setSuccess] = useState(false);
  const [createdProfileId, setCreatedProfileId] = useState(initialDraft?.createdProfileId || null);
  const [createdProfileNo, setCreatedProfileNo] = useState(initialDraft?.createdProfileNo || "");
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState("");
  const [workflowItem, setWorkflowItem] = useState(null);
  const [workflowFiles, setWorkflowFiles] = useState([]);
  const [workflowLastSavedAt, setWorkflowLastSavedAt] = useState("");
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [creatingReferralSource, setCreatingReferralSource] = useState(false);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const workflowSaveTimerRef = useRef(null);

  const progressPct = Math.round(((step + 1) / STEPS.length) * 100);
  const isLast = step === STEPS.length - 1;
  const canPrev = step > 0;
  const profileCreated = Boolean(createdProfileId);
  const duplicateResolutionComplete =
    duplicateMatches.length === 0 ||
    (
      duplicateConfirmed &&
      duplicateAction &&
      (duplicateAction === "retain" || duplicateTargetId)
    );

  const canProceedFromDuplicate =
    duplicateChecked &&
    (
      duplicateMatches.length === 0 ||
      duplicateResolutionComplete
    );
  const isPreparingSelectedFiles = preparingPhotoFiles || preparingDocumentFiles;
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const workflowDraft = useMemo(
    () => ({
      step,
      form,
      duplicateChecked,
      duplicateMatches,
      duplicateAction,
      duplicateTargetId,
      duplicateConfirmed,
      duplicateResolutionMode,
      createdProfileId,
      createdProfileNo,
      savedAt: new Date().toISOString(),
    }),
    [
      step,
      form,
      duplicateChecked,
      duplicateMatches,
      duplicateAction,
      duplicateTargetId,
      duplicateConfirmed,
      duplicateResolutionMode,
      createdProfileId,
      createdProfileNo,
    ]
  );

  const applyDraftState = useCallback((draftSource = null) => {
    setStep(normalizeCreateProfileStep(draftSource?.step));
    setForm(mergeCreateProfileDraftForm(draftSource?.form));
    setErrors({});
    setLoading(false);
    setDuplicateChecked(Boolean(draftSource?.duplicateChecked));
    setDuplicateChecking(false);
    setDuplicateMatches(safeArray(draftSource?.duplicateMatches));
    setDuplicateAction(draftSource?.duplicateAction || "");
    setDuplicateTargetId(draftSource?.duplicateTargetId || "");
    setDuplicateConfirmed(Boolean(draftSource?.duplicateConfirmed));
    setDuplicateResolutionMode(
      draftSource?.duplicateResolutionMode === "later" ? "now" : (draftSource?.duplicateResolutionMode || "")
    );
    setImageFiles([]);
    setPrimaryIndex(0);
    setDocumentFiles([]);
    setDocumentTypeId("");
    setPreparingPhotoFiles(false);
    setPhotoPreparationStatus("");
    setPreparingDocumentFiles(false);
    setDocumentPreparationStatus("");
    setUploadProgress("");
    setDocumentUploadSummary("");
    setSuccess(false);
    setCreatedProfileId(draftSource?.createdProfileId || null);
    setCreatedProfileNo(draftSource?.createdProfileNo || "");
  }, []);

  useEffect(() => {
    if (!freshStartKey) return;

    clearCreateProfileDraft(draftStorageKey);
    setStep(DUPLICATE_STEP_INDEX);
    setForm(defaultForm);
    setErrors({});
    setLoading(false);
    setDuplicateChecked(false);
    setDuplicateChecking(false);
    setDuplicateMatches([]);
    setDuplicateAction("");
    setDuplicateTargetId("");
    setDuplicateConfirmed(false);
    setDuplicateResolutionMode("");
    setImageFiles([]);
    setPrimaryIndex(0);
    setDocumentFiles([]);
    setDocumentTypeId("");
    setPreparingPhotoFiles(false);
    setPhotoPreparationStatus("");
    setPreparingDocumentFiles(false);
    setDocumentPreparationStatus("");
    setUploadProgress("");
    setDocumentUploadSummary("");
    setSuccess(false);
    setCreatedProfileId(null);
    setCreatedProfileNo("");
    setWorkflowError("");
    setWorkflowLastSavedAt("");
  }, [draftStorageKey, freshStartKey]);

  useEffect(() => {
    if (!draftStorageKey || success) return;

    try {
      window.localStorage.setItem(draftStorageKey, JSON.stringify(workflowDraft));
    } catch (err) {
      console.warn("Unable to persist create profile draft", err);
    }
  }, [
    draftStorageKey,
    workflowDraft,
    success,
  ]);

  useEffect(() => {
    if (!isBiodataWorkflow || !workflowItemId) {
      setWorkflowLoading(false);
      setWorkflowError("");
      setWorkflowItem(null);
      setWorkflowFiles([]);
      setWorkflowLastSavedAt("");
      return undefined;
    }

    let cancelled = false;

    const loadWorkflow = async () => {
      try {
        setWorkflowLoading(true);
        setWorkflowError("");
        const response = await API.get(`/biodata-assignments/${workflowItemId}/workspace`, authHeaders);
        if (cancelled) return;

        const localDraft = readCreateProfileDraft(draftStorageKey);
        const remoteDraft = response.data?.draft?.payload || null;
        const chosenDraft = remoteDraft || localDraft || null;

        setWorkflowItem(response.data?.item || null);
        setWorkflowFiles(safeArray(response.data?.files));
        setWorkflowLastSavedAt(response.data?.draft?.last_saved_at || chosenDraft?.savedAt || "");
        applyDraftState(chosenDraft);
      } catch (err) {
        if (cancelled) return;
        setWorkflowItem(null);
        setWorkflowFiles([]);
        setWorkflowError(err.response?.data?.error || "Failed to load biodata workspace.");
      } finally {
        if (!cancelled) setWorkflowLoading(false);
      }
    };

    loadWorkflow();

    return () => {
      cancelled = true;
    };
  }, [applyDraftState, authHeaders, draftStorageKey, isBiodataWorkflow, workflowItemId]);

  useEffect(() => {
    if (!isBiodataWorkflow || !workflowItemId || workflowLoading || success) return undefined;

    window.clearTimeout(workflowSaveTimerRef.current);
    workflowSaveTimerRef.current = window.setTimeout(async () => {
      try {
        setWorkflowSaving(true);
        await API.put(
          `/biodata-assignments/${workflowItemId}/draft`,
          { draft_payload: workflowDraft },
          authHeaders
        );
        setWorkflowLastSavedAt(workflowDraft.savedAt || new Date().toISOString());
      } catch (err) {
        console.warn("Unable to save biodata draft", err);
      } finally {
        setWorkflowSaving(false);
      }
    }, 700);

    return () => {
      window.clearTimeout(workflowSaveTimerRef.current);
    };
  }, [authHeaders, isBiodataWorkflow, success, workflowDraft, workflowItemId, workflowLoading]);

  useEffect(() => {
    Promise.all([
      API.get("/genders"),
      API.get("/locations"),
      API.get("/city-tiers"),
      API.get("/marital-status"),
      API.get("/complexions"),
      API.get("/blood-groups"),
      API.get("/tags"),
      API.get("/castes"),
      API.get("/sakhas"),
      API.get("/sampradays"),
      API.get("/education-categories"),
      API.get("/degree-names"),
      API.get("/occupation-types"),
      API.get("/relation-types"),
      API.get("/document-types"),
      API.get("/referral-sources", { headers: { Authorization: `Bearer ${token}` } }),
      API.get("/visa-statuses"),
      API.get("/visa-names"),
    ])
      .then(([g, l, cityTiersRes, ms, c, bg, t, ca, sk, sp, ec, dn, ot, rt, dt, referralSourcesRes, vs, vn]) => {
        const nextLookups = {
          genders: safeArray(g.data),
          locations: safeArray(l.data),
          cityTiers: safeArray(cityTiersRes.data),
          maritalStatus: safeArray(ms.data),
          complexions: safeArray(c.data),
          bloodGroups: safeArray(bg.data),
          occupationTypes: safeArray(ot.data),
          relationTypes: safeArray(rt.data),
          tags: safeArray(t.data),
          castes: safeArray(ca.data),
          sakhas: safeArray(sk.data),
          sampradays: safeArray(sp.data),
          educationCategories: safeArray(ec.data),
          degreeNames: safeArray(dn.data),
          documentTypes: safeArray(dt.data),
          referralSources: safeArray(referralSourcesRes.data),
          visaStatuses: safeArray(vs.data),
          visaNames: safeArray(vn.data),
        };
        const defaultCasteId = findConfigIdByLabel(nextLookups.castes, "Jain");
        setLookups(nextLookups);
        setForm((prev) => {
          const nextForm = withResolvedRelativeRelationTypeIds(prev, nextLookups.relationTypes);
          if (nextForm.caste_id || !defaultCasteId) return nextForm;
          return {
            ...nextForm,
            caste_id: String(defaultCasteId),
          };
        });
      })
      .catch((err) => {
        console.error("Lookup load failed", err);
        alert("Failed to load form dropdowns.");
      });
  }, [token]);

  const filteredSakhas = useMemo(() => {
    if (!form.caste_id) return lookups.sakhas;
    return lookups.sakhas.filter((s) => Number(s.caste_id) === Number(form.caste_id));
  }, [lookups.sakhas, form.caste_id]);

  const familyRelationOptions = useMemo(
    () => safeArray(lookups.relationTypes).filter((relationType) => isFamilyRelationType(relationType.name || relationType.display_label)),
    [lookups.relationTypes]
  );

  const sideRelativeRelationOptions = useMemo(
    () => safeArray(lookups.relationTypes).filter((relationType) => !isFamilyRelationType(relationType.name || relationType.display_label)),
    [lookups.relationTypes]
  );

  const familyEntries = useMemo(
    () => safeArray(form.relatives)
      .map((relative, index) => ({ relative: normalizeRelativeForSection(relative, lookups.relationTypes), index }))
      .filter((item) => isFamilySectionRelative(item.relative, lookups.relationTypes)),
    [form.relatives, lookups.relationTypes]
  );

  const sideRelativeEntries = useMemo(
    () => safeArray(form.relatives)
      .map((relative, index) => ({ relative: normalizeRelativeForSection(relative, lookups.relationTypes), index }))
      .filter((item) => !isFamilySectionRelative(item.relative, lookups.relationTypes)),
    [form.relatives, lookups.relationTypes]
  );

  const preferredCityOptions = useMemo(() => {
    const cityTierMap = buildCityTierMap(lookups.cityTiers);
    return uniqueBy(
      safeArray(lookups.locations)
        .map((location) => ({
          id: Number(location.id),
          label: getCompactLocationOptionLabel(location),
          tierIds: cityTierMap.get(Number(location.id)) || [],
        }))
        .filter((option) => option.id && option.label),
      "id"
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.cityTiers, lookups.locations]);

  const preferredSakhaOptions = useMemo(() => {
    return safeArray(lookups.sakhas)
      .map((sakha) => ({ id: Number(sakha.id), label: sakha.name || "" }))
      .filter((option) => option.id && option.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.sakhas]);

  const preferredEducationOptions = useMemo(() => {
    return safeArray(lookups.educationCategories)
      .map((category) => ({ id: Number(category.id), label: category.name || "" }))
      .filter((option) => option.id && option.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.educationCategories]);

  const preferredDegreeOptions = useMemo(() => {
    return uniqueBy(
      safeArray(lookups.degreeNames)
        .map((degree) => {
          const label = degree.name || degree.display_label || "";
          return { id: label, label };
        })
        .filter((option) => option.id && option.label),
      "id"
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.degreeNames]);

  const preferredOccupationOptions = useMemo(() => {
    return uniqueBy(
      safeArray(lookups.occupationTypes)
        .map((occupationType) => {
          const label = occupationType.name || occupationType.display_label || "";
          return { id: label, label };
        })
        .filter((option) => option.id && option.label),
      "id"
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.occupationTypes]);

  const tagOptions = useMemo(() => {
    return safeArray(lookups.tags)
      .map((tag) => ({ id: Number(tag.id), label: tag.name || "" }))
      .filter((option) => option.id && option.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lookups.tags]);

  const set = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "dob" || field === "birth_date") {
        next.dob = value;
        next.birth_date = value;
      }

      if (field === "height" || field === "height_cm") {
        next.height = value;
        next.height_cm = value;
      }

      if (field === "marital_status_id" && isSingleMaritalStatusId(value, lookups.maritalStatus)) {
        next.has_children = false;
        next.marital_field_details = "";
      }

      applyLocationCascade(next, field, value, lookups.locations);

      if (field === "caste_id") {
        next.sakha_id = "";
        next.dadisa_sakha_id = "";
        next.nanisa_sakha_id = "";
        next.mamasa_sakha_id = "";
      }

      return next;
    });

    if (isIdentityField(field)) resetDuplicateState();
  };

  const setNested = (parent, field, value) => {
    setForm((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const setOccupation = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      occupations: (prev.occupations || [{ ...emptyOccupation, is_primary: true, sort_order: 1 }]).map((occupation, occupationIndex) => {
        if (occupationIndex !== index) return occupation;

        const nextOccupation = {
          ...occupation,
          [field]: value,
        };

        if (field === "occupation_type_id" || field === "occupation_type") {
          const occupationTypeLabel =
            field === "occupation_type"
              ? value
              : getLookupName(lookups.occupationTypes, value) || occupation.occupation_type || "";

          if (isServiceOccupationTypeLabel(occupationTypeLabel)) {
            nextOccupation.annual_turnover_amount = "";
            nextOccupation.annual_turnover_currency_code = "INR";
          } else {
            nextOccupation.annual_income_amount = "";
            nextOccupation.annual_income_currency_code = "INR";
          }
        }

        return nextOccupation;
      }),
    }));
  };

  const addOccupation = () => {
    setForm((prev) => ({
      ...prev,
      occupations: [
        ...(prev.occupations || []),
        { ...emptyOccupation, sort_order: (prev.occupations || []).length + 1 },
      ],
    }));
  };

  const removeOccupation = (index) => {
    setForm((prev) => {
      const occupations = (prev.occupations || []).filter((_, occupationIndex) => occupationIndex !== index);
      const nextOccupations = occupations.length
        ? occupations.map((occupation, occupationIndex) => ({
            ...occupation,
            is_primary: occupationIndex === 0,
            sort_order: occupationIndex + 1,
          }))
        : [{ ...emptyOccupation, is_primary: true, sort_order: 1 }];

      return {
        ...prev,
        occupations: nextOccupations,
      };
    });
  };

  const setEducation = (index, field, value) => {
    setForm((prev) => {
      const educations = [...prev.educations];
      educations[index] = { ...educations[index], [field]: value };

      if (field === "education_category_id") {
        educations[index].degree_name = "";
      }

      if (field === "education_city_id") {
        const city = lookups.locations.find((l) => Number(l.id) === Number(value));
        if (city) {
          educations[index].education_country_id = getLocationCountryId(city);
          educations[index].education_state_id = city.state_id || "";
          educations[index].education_district_id = city.district_id || "";
          educations[index].education_tehsil_id = city.tehsil_id || "";
          educations[index].education_location = getLocationOptionLabel(city);
        } else if (!value) {
          educations[index].education_country_id = "";
          educations[index].education_state_id = "";
          educations[index].education_district_id = "";
          educations[index].education_tehsil_id = "";
          educations[index].education_location = "";
        }
      }

      return { ...prev, educations };
    });
  };

  const addEducation = () => {
    setForm((prev) => ({
      ...prev,
      educations: [...prev.educations, { ...emptyEducation, sort_order: prev.educations.length + 1 }],
    }));
  };

  const removeEducation = (index) => {
    setForm((prev) => ({
      ...prev,
      educations: prev.educations.filter((_, i) => i !== index),
    }));
  };

  const setRelative = (index, field, value) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      let nextRelative = normalizeRelativeForSection({ ...relatives[index], [field]: value }, lookups.relationTypes);

      if (field === "relation_type_id" || field === "relation_type") {
        nextRelative = applyRelativeGenderMapping(nextRelative, lookups.relationTypes);
      }

      if ((field === "relation_type_id" || field === "relation_type") && !shouldShowRelativeMaritalStatus(nextRelative, lookups.relationTypes)) {
        nextRelative = clearRelativeMaritalFields(nextRelative);
      }

      if (field === "marital_status" && !isRelativeUnmarriedStatus(value)) {
        nextRelative = clearRelativeFutureProspectFields(nextRelative);
      }

      applyRelativeLocationCascade(nextRelative, field, value, lookups.locations);
      nextRelative = applySiblingNameDefaults(nextRelative, relatives, index, lookups.relationTypes);
      relatives[index] = nextRelative;
      return { ...prev, relatives };
    });
  };

  const addRelative = () => {
    setForm((prev) => ({
      ...prev,
      relatives: [...prev.relatives, { ...emptyRelative, family_side: "paternal", sort_order: prev.relatives.length + 1 }],
    }));
  };

  const addFamilyMember = () => {
    setForm((prev) => ({
      ...prev,
      relatives: [...prev.relatives, { ...emptyRelative, family_side: "none", sort_order: prev.relatives.length + 1 }],
    }));
  };

  const setAddress = (index, field, value) => {
    setForm((prev) => {
      const addresses = [...prev.addresses];
      const nextAddress = { ...addresses[index], [field]: value };
      applyAddressLocationCascade(nextAddress, field, value);
      syncAddressCountryFromLocations(nextAddress, lookups.locations);
      syncAddressVisaFields(nextAddress, lookups.locations);
      addresses[index] = nextAddress;
      return { ...prev, addresses };
    });
  };

  const addAddress = () => {
    setForm((prev) => ({
      ...prev,
      addresses: [...prev.addresses, { ...emptyAddress }],
    }));
  };

  const removeAddress = (index) => {
    setForm((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const removeRelative = (index) => {
    setForm((prev) => ({
      ...prev,
      relatives: prev.relatives.filter((_, i) => i !== index),
    }));
  };

  const setRelativeContact = (relativeIndex, contactIndex, field, value) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const contacts = [...(relatives[relativeIndex].contacts || [])];
      contacts[contactIndex] = { ...contacts[contactIndex], [field]: value };
      relatives[relativeIndex] = { ...relatives[relativeIndex], contacts };
      return { ...prev, relatives };
    });
  };

  const addRelativeContact = (relativeIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        contacts: [...(relatives[relativeIndex].contacts || []), { contact_type: "mobile", contact_value: "" }],
      };
      return { ...prev, relatives };
    });
  };

  const removeRelativeContact = (relativeIndex, contactIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        contacts: (relatives[relativeIndex].contacts || []).filter((_, index) => index !== contactIndex),
      };
      return { ...prev, relatives };
    });
  };

  const setRelativeEducation = (relativeIndex, eduIndex, field, value) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      const education = [...(relatives[relativeIndex].education || [])];
      education[eduIndex] = { ...education[eduIndex], [field]: value };
      if (field === "education_category_id") {
        education[eduIndex].degree_name = "";
      }
      relatives[relativeIndex] = { ...relatives[relativeIndex], education };
      return { ...prev, relatives };
    });
  };

  const addRelativeEducation = (relativeIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        education: [...(relatives[relativeIndex].education || []), { ...emptyRelativeEducation, is_highest_degree: false }],
      };
      return { ...prev, relatives };
    });
  };

  const removeRelativeEducation = (relativeIndex, eduIndex) => {
    setForm((prev) => {
      const relatives = [...prev.relatives];
      relatives[relativeIndex] = {
        ...relatives[relativeIndex],
        education: (relatives[relativeIndex].education || []).filter((_, i) => i !== eduIndex),
      };
      return { ...prev, relatives };
    });
  };

  const toggleTag = (id) => {
    const tagId = Number(id);
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((x) => x !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const createTag = async (label) => {
    const name = String(label || "").trim();
    if (!name) return;

    const { data } = await API.post("/tags", { name }, authHeaders);
    const tagId = Number(data?.id);
    const tagName = data?.name || name;
    if (!tagId) throw new Error("Tag was created but no tag ID was returned.");

    setLookups((prev) => {
      const tags = safeArray(prev.tags);
      const exists = tags.some((tag) => Number(tag.id) === tagId);
      const nextTags = exists ? tags : [...tags, { ...data, id: tagId, name: tagName }];
      return {
        ...prev,
        tags: nextTags.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
      };
    });

    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId) ? prev.tags : [...prev.tags, tagId],
    }));
  };

  const createReferralSource = async (label) => {
    const name = String(label || "")
      .trim()
      .replace(/\s+/g, " ");
    if (!name || creatingReferralSource) return;

    try {
      setCreatingReferralSource(true);
      const { data } = await API.post("/referral-sources", { name }, authHeaders);
      const item = data?.item || null;
      const referralSourceId = Number(item?.id);
      if (!referralSourceId) {
        throw new Error("Referral source was created but no ID was returned.");
      }

      setLookups((prev) => ({
        ...prev,
        referralSources: upsertReferralSource(prev.referralSources, item),
      }));
      set("referral_source_id", String(referralSourceId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create referral source.");
    } finally {
      setCreatingReferralSource(false);
    }
  };

  const togglePrefValue = (field, value) => {
    setForm((prev) => {
      const current = prev.preferences[field] || [];
      const hasValue = current.some((item) => String(item) === String(value));
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [field]: hasValue
            ? current.filter((item) => String(item) !== String(value))
            : [...current, value],
        },
      };
    });
  };

  const resetDuplicateState = () => {
    setDuplicateChecked(false);
    setDuplicateMatches([]);
    setDuplicateAction("");
    setDuplicateTargetId("");
    setDuplicateConfirmed(false);
    setDuplicateResolutionMode("");
  };

  const validateDuplicateFields = () => {
    const nextErrors = {};

    if (!form.first_name.trim()) nextErrors.first_name = "Required";
    if (!form.middle_name.trim()) nextErrors.middle_name = "Required";
    if (!form.last_name.trim()) nextErrors.last_name = "Required";
    if (!form.gender_id && !form.gender) nextErrors.gender_id = "Required";
    if (!form.birth_date && !form.dob) nextErrors.dob = "Required";
    const birthDate = form.birth_date || form.dob;
    if (birthDate) {
      const age = getAge(birthDate);
      if (age !== null && age < 18) nextErrors.dob = "Must be at least 18";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep = (targetStep = step) => {
    const nextErrors = {};
    const isFinalStep = targetStep >= STEPS.length - 1;

    if (targetStep >= 0) {
      if (!duplicateChecked) nextErrors.duplicate = "Run duplicate check first";
      if (duplicateMatches.length > 0 && !duplicateResolutionComplete) {
        nextErrors.duplicate = isFinalStep
          ? "Resolve duplicate before finishing the profile."
          : "Resolve duplicate here before continuing. Choose Retain, Merge, or Replace and confirm review.";
      }
    }

    if (targetStep >= 0) {
      if (!form.gender_id && !form.gender) nextErrors.gender_id = "Required";
      if (!form.birth_date && !form.dob) nextErrors.dob = "Required";
    }

    if (targetStep >= 1) {
      if (!form.first_name.trim()) nextErrors.first_name = "Required";
      if (!form.middle_name.trim()) nextErrors.middle_name = "Required";
      if (!form.last_name.trim()) nextErrors.last_name = "Required";
    }

    if (targetStep >= ADDRESS_CONTACTS_STEP_INDEX) {
      const contactError = validatePhoneContacts(form.contacts);
      if (contactError) nextErrors.contacts = contactError;
    }

    if (targetStep >= RELATIVES_STEP_INDEX) {
      const futureProspectError = validateFutureProspectRelatives(form.relatives);
      if (futureProspectError) nextErrors.relatives = futureProspectError;
      const relativeContactError = validateRelativePhoneContacts(form.relatives);
      if (!nextErrors.relatives && relativeContactError) nextErrors.relatives = relativeContactError;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleDuplicateCheck = async () => {
    if (!validateDuplicateFields()) return;

    setDuplicateChecking(true);
    setDuplicateChecked(false);
    setDuplicateMatches([]);
    setDuplicateAction("");
    setDuplicateTargetId("");
    setDuplicateConfirmed(false);
    setDuplicateResolutionMode("");

    try {
      const genderLabel = getLookupName(lookups.genders, form.gender_id) || form.gender;
      const response = await API.get("/profiles/duplicate-check", {
        params: {
          first_name: form.first_name || undefined,
          middle_name: form.middle_name || undefined,
          last_name: form.last_name || undefined,
          gender: genderLabel || undefined,
          birth_date: form.birth_date || form.dob || undefined,
          birth_time: form.birth_time || undefined,
          source: "create_profile_duplicate_check",
          limit: 100,
        },
      });

      const unique = Array.isArray(response.data) ? response.data : [];

      const source = {
        first_name: form.first_name,
        middle_name: form.middle_name,
        last_name: form.last_name,
        gender: genderLabel,
        birth_date: form.birth_date || form.dob,
        birth_time: form.birth_time,
      };

      const scored = unique
        .map((candidate) => scoreDuplicate(source, candidate))
        .filter((x) => x.strictMatch && (x.score > 0 || (x.reasons || []).length > 0))
        .sort((a, b) => b.score - a.score)

      setDuplicateMatches(scored);
      setDuplicateResolutionMode(scored.length > 0 ? "now" : "");
      setDuplicateChecked(true);
      console.log("[CreateProfile] duplicate check completed", {
        totalCandidates: unique.length,
        matches: scored.length,
      });
    } catch (err) {
      console.error("Duplicate check failed", err);
      alert("Duplicate check failed: " + (err.response?.data?.error || err.message));
    } finally {
      setDuplicateChecking(false);
    }
  };

  const handleFileSelect = async (event) => {
    const input = event.target;
    const selected = Array.from(input.files || []);
    setPhotoPreparationStatus("");
    const usable = await prepareFilesForUpload(selected, {
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
      allowedTypeLabel: "JPEG, PNG and WEBP",
      fileKindLabel: "photo",
      onStatusChange: ({ active, message }) => {
        setPreparingPhotoFiles(active);
        setPhotoPreparationStatus(active ? message : "");
      },
    });

    setImageFiles((prev) =>
      [
        ...prev,
        ...usable.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
          name: file.name,
        })),
      ].slice(0, 10)
    );

    setPreparingPhotoFiles(false);
    setPhotoPreparationStatus("");
    input.value = "";
  };

  const removeImage = (index) => {
    setImageFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });

    if (primaryIndex === index) setPrimaryIndex(0);
    else if (primaryIndex > index) setPrimaryIndex((x) => x - 1);
  };

  const handleDocumentSelect = async (event) => {
    const input = event.target;
    const selected = Array.from(input.files || []);
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "image/jpeg",
      "image/png",
    ];

    setDocumentUploadSummary("");
    setDocumentPreparationStatus("");
    const usable = await prepareFilesForUpload(selected, {
      allowedTypes,
      allowedTypeLabel: "PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG and PNG",
      fileKindLabel: "document",
      maxBytes: MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES,
      autoCompressOversized: true,
      onStatusChange: ({ active, message }) => {
        setPreparingDocumentFiles(active);
        setDocumentPreparationStatus(active ? message : "");
      },
    });

    setDocumentFiles((prev) =>
      [
        ...prev,
        ...usable.map((file) => ({
          file,
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      ].slice(0, 10)
    );

    setPreparingDocumentFiles(false);
    setDocumentPreparationStatus("");
    input.value = "";
  };

  const removeDocument = (index) => {
    setDocumentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const setContact = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      contacts: (prev.contacts || []).map((contact, contactIndex) =>
        contactIndex === index
          ? { ...contact, [field]: value }
          : contact
      ),
    }));
  };

  const addContact = () => {
    setForm((prev) => ({
      ...prev,
      contacts: [...(prev.contacts || []), { ...emptyContact }],
    }));
  };

  const removeContact = (index) => {
    setForm((prev) => ({
      ...prev,
      contacts: (prev.contacts || []).filter((_, contactIndex) => contactIndex !== index),
    }));
  };

  const resolveProfileIdentity = async (profileId, responseData = {}) => {
    const responseProfileNo =
      responseData?.profileNo ||
      responseData?.profile_no ||
      responseData?.profile?.profile_no ||
      responseData?.data?.profile_no ||
      "";

    if (responseProfileNo) {
      return {
        profileId,
        profileNo: String(responseProfileNo),
      };
    }

    try {
      const profileRes = await API.get(`/profiles/${profileId}`, authHeaders);
      return {
        profileId,
        profileNo: String(profileRes?.data?.profile_no || profileRes?.data?.profileNo || ""),
      };
    } catch (err) {
      console.warn("Unable to fetch created profile number", err);
      return {
        profileId,
        profileNo: "",
      };
    }
  };

  const syncCreatedProfileIdentity = async (profileId, responseData = {}) => {
    if (!profileId) return { profileId: null, profileNo: "" };
    const identity = await resolveProfileIdentity(profileId, responseData);
    setCreatedProfileId(identity.profileId);
    setCreatedProfileNo(identity.profileNo);
    return identity;
  };

  const openWorkflowFile = async (file) => {
    if (!workflowItemId || !file?.id) return;

    try {
      const response = await API.get(
        `/biodata-assignments/${workflowItemId}/files/${file.id}/file`,
        {
          ...authHeaders,
          responseType: "blob",
        }
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to open biodata file.");
    }
  };

  const handleDuplicateStepCreate = async () => {
    if (!validateStep(DUPLICATE_STEP_INDEX)) {
      return;
    }

    setLoading(true);

    try {
      const basePayload = buildInitialProfilePayload(form, lookups);
      const duplicateReview = duplicateMatches.length
        ? buildDuplicateReviewPayload({
            duplicateMatches,
            duplicateAction,
            duplicateTargetId,
            duplicateResolutionMode,
          })
        : null;

      if (profileCreated) {
        setUploadProgress("Updating profile basics...");
        await API.put(
          `/profiles/${createdProfileId}`,
          {
            ...basePayload,
            duplicate_review: duplicateReview,
            create_flow: true,
          },
          authHeaders
        );
        await syncCreatedProfileIdentity(createdProfileId);
      } else {
        setUploadProgress("Creating profile...");
        const { data } = await API.post(
          "/profiles",
          {
            ...basePayload,
            duplicate_review: duplicateReview,
          },
          authHeaders
        );

        const profileId = data?.profileId || data?.id || data?.profile_id || data?.profile?.id;
        if (!profileId) {
          throw new Error("Profile was created but no profile ID was returned.");
        }
        const identity = await syncCreatedProfileIdentity(profileId, data);

        if (duplicateMatches.length > 0 && identity.profileId) {
          API.post(
            `/profiles/${identity.profileId}/duplicate-review`,
            duplicateReview,
            authHeaders
          ).catch(() => {});
        }
      }

      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.profile_save;
        return nextErrors;
      });
      setStep(BASIC_STEP_INDEX);
    } catch (err) {
      console.error(err);
      alert("Failed: " + (err?.response?.data?.detail || err?.response?.data?.error || err.message));
    } finally {
      setUploadProgress("");
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!createdProfileId) {
      setErrors((prev) => ({
        ...prev,
        profile_save: "Create the profile from Duplicate Check before completing the remaining sections.",
      }));
      setStep(DUPLICATE_STEP_INDEX);
      return;
    }

    if (!validateStep(STEPS.length - 1)) {
      if (!duplicateChecked) setStep(DUPLICATE_STEP_INDEX);
      else if (duplicateMatches.length > 0 && !duplicateResolutionComplete) setStep(STEPS.length - 1);
      return;
    }

    setLoading(true);
    setDocumentUploadSummary("");

    try {
      setUploadProgress("Saving profile...");

      const duplicateReview = duplicateMatches.length
        ? buildDuplicateReviewPayload({
            duplicateMatches,
            duplicateAction,
            duplicateTargetId,
            duplicateResolutionMode,
          })
        : null;
      const payload = {
        ...buildPayload(form, lookups),
        duplicate_review: duplicateReview,
        create_flow: true,
      };
      await API.put(
        `/profiles/${createdProfileId}`,
        payload,
        authHeaders
      );

      const profileId = createdProfileId;
      await syncCreatedProfileIdentity(profileId);

      if (duplicateMatches.length > 0 && duplicateReview) {
        API.post(
          `/profiles/${profileId}/duplicate-review`,
          duplicateReview,
          authHeaders
        ).catch(() => {});
      }

      if (imageFiles.length > 0) {
        setUploadProgress(`Uploading ${imageFiles.length} photo${imageFiles.length > 1 ? "s" : ""}...`);
        const formData = new FormData();
        imageFiles.forEach(({ file }) => {
          formData.append("images", file, file.name || "profile-photo");
        });
        formData.append("primary_image_index", String(primaryIndex));
        formData.append("primaryImageIndex", String(primaryIndex));
        formData.append("create_flow", "true");

        const baseURL = API.defaults?.baseURL || "/api";
        const uploadResponse = await fetch(`${baseURL}/profiles/${profileId}/images`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const uploadData = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(uploadData.error || "Photo upload failed");
        }
      }

      if (documentFiles.length > 0) {
        if (!documentTypeId) {
          throw new Error("Please select a document type before uploading documents.");
        }

        setUploadProgress(`Uploading ${documentFiles.length} document${documentFiles.length > 1 ? "s" : ""}...`);
        const uploadedDocuments = await uploadProfileDocuments({
          profileId,
          documentTypeId,
          documentFiles,
          token,
          createFlow: true,
          onProgress: ({ current, total }) => {
            setUploadProgress(`Uploading document ${current} of ${total}...`);
          },
        });
        setDocumentUploadSummary(buildDocumentUploadSummary(uploadedDocuments));
      }

      if (isBiodataWorkflow && workflowItemId) {
        setUploadProgress("Completing biodata assignment...");
        await API.post(
          `/biodata-assignments/${workflowItemId}/complete`,
          {
            profile_id: profileId,
            duplicate_review: duplicateReview,
          },
          authHeaders
        );
      }

      setUploadProgress("");
      clearCreateProfileDraft(draftStorageKey);
      setSuccess(true);
      setForm(withResolvedRelativeRelationTypeIds(defaultForm, lookups.relationTypes));
      setImageFiles([]);
      setPrimaryIndex(0);
      setDocumentFiles([]);
      setDocumentTypeId("");
      setStep(DUPLICATE_STEP_INDEX);
      setDuplicateChecked(false);
      setDuplicateMatches([]);
      setDuplicateAction("");
      setDuplicateConfirmed(false);
      setDuplicateResolutionMode("");
      setCreatedProfileNo("");

      if (onDone) setTimeout(() => onDone(), 900);
    } catch (err) {
      console.error(err);
      alert("Failed: " + (err?.response?.data?.detail || err?.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const goNext = () => {
    if (step === DUPLICATE_STEP_INDEX && !canProceedFromDuplicate) {
      setErrors((p) => ({
        ...p,
        duplicate: "Run duplicate check first. If duplicates are found, resolve them here before continuing.",
      }));
      return;
    }

    if (step > DUPLICATE_STEP_INDEX && !canProceedFromDuplicate) {
      setErrors((p) => ({
        ...p,
        duplicate: "Identity fields changed. Run duplicate check again and resolve duplicates there before continuing.",
      }));
      setStep(DUPLICATE_STEP_INDEX);
      return;
    }

    if (step > DUPLICATE_STEP_INDEX && !profileCreated) {
      setErrors((p) => ({
        ...p,
        profile_save: "Create Profile from Duplicate Check first so the profile ID and number are created before you continue.",
      }));
      setStep(DUPLICATE_STEP_INDEX);
      return;
    }

    if (!validateStep(step)) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  if (success) {
    return (
      <div style={S.successWrap}>
        <div style={S.successCard}>
          <div style={S.successIcon}>OK</div>
          <h2 style={S.successTitle}>{isBiodataWorkflow ? "Profile Created And Biodata Closed" : "Profile Created"}</h2>
          <p style={S.successSub}>
            {createdProfileNo ? <>Profile <b>#{createdProfileNo}</b> has been successfully registered.</> : "Profile has been successfully registered."}
          </p>
          {isBiodataWorkflow ? (
            <p style={S.successSub}>The assigned biodata has been attached to the profile and removed from the active operator queue.</p>
          ) : null}
          {documentUploadSummary ? <p style={S.successSub}>{documentUploadSummary}</p> : null}
          <button
            style={S.primaryBtn}
            onClick={() => {
              setSuccess(false);
              setCreatedProfileId(null);
              setCreatedProfileNo("");
              setDocumentUploadSummary("");
              clearCreateProfileDraft(draftStorageKey);
              if (isBiodataWorkflow && typeof onDone === "function") {
                onDone();
              }
            }}
          >
            {isBiodataWorkflow ? "Return To Bio Data Assignments" : "Create Another Profile"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .jk-grid-2 { grid-template-columns: 1fr !important; }
          .jk-stepper {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
          }
          .jk-step-btn {
            flex: 0 0 auto !important;
            min-width: 132px !important;
            max-width: none !important;
          }
        }
        @media (max-width: 980px) {
          .jk-duplicate-resolve-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={S.wrap}>
        <div style={S.hero}>
          <div>
            <p style={S.eyebrow}>Jain Kalyanam CRM</p>
            <h1 style={S.headerTitle}>{isBiodataWorkflow ? "Bio Data Entry Workspace" : "Create New Profile"}</h1>
            <p style={S.headerSub}>
              {isBiodataWorkflow
                ? "Review the assigned raw biodata, enter the structured profile details, and complete the assignment only after duplicate review is resolved."
                : "Duplicate-safe, schema-aligned profile creation with modern validation and location hierarchy."}
            </p>

            <div style={S.titleProgressTrack}>
              <div style={{ ...S.titleProgressFill, width: `${progressPct}%` }} />
            </div>
            <div style={S.titleProgressText}>
              {STEPS[step].label} - Step {step + 1} of {STEPS.length} - {progressPct}%
            </div>
          </div>

          <div style={S.heroBadge}>
            {profileCreated
              ? createdProfileNo
                ? `Profile #${createdProfileNo}`
                : "Profile saved"
              : duplicateChecked
                ? duplicateMatches.length
                  ? `${duplicateMatches.length} duplicate candidate(s)`
                  : "No duplicate found"
                : "Duplicate check pending"}
          </div>
        </div>

        {isBiodataWorkflow && (
          <div style={S.workflowCard}>
            <div style={S.workflowHeader}>
              <div>
                <div style={S.workflowTitle}>
                  {workflowItem?.source_label || "Assigned Biodata"}
                </div>
                <div style={S.workflowSub}>
                  {workflowItem?.assigned_by_email
                    ? `Assigned by ${workflowItem.assigned_by_email}`
                    : "Assigned biodata intake item"}
                  {workflowItem?.created_at ? ` • Uploaded ${formatDateTimeLabel(workflowItem.created_at)}` : ""}
                </div>
              </div>
              <div style={S.workflowBadge}>
                {workflowSaving
                  ? "Saving draft..."
                  : workflowLastSavedAt
                    ? `Draft saved ${formatDateTimeLabel(workflowLastSavedAt)}`
                    : workflowLoading
                      ? "Loading workspace..."
                      : "Draft not saved yet"}
              </div>
            </div>

            {workflowError ? <div style={S.errorBanner}>{workflowError}</div> : null}
            {workflowItem?.notes ? <div style={S.workflowNotes}>{workflowItem.notes}</div> : null}

            <div style={S.workflowFileRow}>
              {workflowLoading ? (
                <div style={S.workflowEmpty}>Loading biodata files...</div>
              ) : workflowFiles.length > 0 ? (
                workflowFiles.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    style={S.workflowFileBtn}
                    onClick={() => openWorkflowFile(file)}
                  >
                    {file.original_file_name || `Biodata file ${file.id}`}
                  </button>
                ))
              ) : (
                <div style={S.workflowEmpty}>
                  Raw biodata files will be moved into the created profile automatically when this assignment is completed.
                </div>
              )}
            </div>
          </div>
        )}

        <div style={S.stepper} className="jk-stepper">
          {STEPS.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="jk-step-btn"
              style={{
                ...S.stepBtn,
                ...(index === step ? S.stepActive : {}),
                ...(index < step ? S.stepDone : {}),
              }}
              onClick={() => {
                if (index > DUPLICATE_STEP_INDEX && !canProceedFromDuplicate) {
                  setErrors((p) => ({
                    ...p,
                    duplicate: "Please complete duplicate check before continuing.",
                  }));
                  return;
                }
                if (index > DUPLICATE_STEP_INDEX && !profileCreated) {
                  setErrors((p) => ({
                    ...p,
                    profile_save: "Create Profile from Duplicate Check before opening the remaining sections.",
                  }));
                  return;
                }
                setStep(index);
              }}
            >
              <span style={S.stepNo}>{index < step ? "Done" : index + 1}</span>
              <span style={S.stepLabel}>{item.navLabel || item.label}</span>
            </button>
          ))}
        </div>

        <div style={S.panel}>
          {step === 0 && (
            <DuplicateCheckGate
              form={form}
              set={set}
              errors={errors}
              lookups={lookups}
              duplicateChecking={duplicateChecking}
              duplicateChecked={duplicateChecked}
              duplicateMatches={duplicateMatches}
              duplicateAction={duplicateAction}
              setDuplicateAction={setDuplicateAction}
              duplicateTargetId={duplicateTargetId}
              setDuplicateTargetId={setDuplicateTargetId}
              duplicateConfirmed={duplicateConfirmed}
              setDuplicateConfirmed={setDuplicateConfirmed}
              handleDuplicateCheck={handleDuplicateCheck}
              onViewProfile={onViewProfile}
            />
          )}

          {step === BASIC_STEP_INDEX && (
            <StepShell title="Profile Basics" progress={progressPct}>
              <div style={S.grid2} className="jk-grid-2">
                <Field label="First Name *" error={errors.first_name}>
                  <input style={{ ...S.input, ...(errors.first_name ? S.inputErr : {}) }} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Naman" />
                </Field>

                <Field label="Middle Name *" error={errors.middle_name}>
                  <input style={{ ...S.input, ...(errors.middle_name ? S.inputErr : {}) }} value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} placeholder="Sunil" />
                </Field>

                <Field label="Last Name *" error={errors.last_name}>
                  <input style={{ ...S.input, ...(errors.last_name ? S.inputErr : {}) }} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Doshi" />
                </Field>

                <Field label="Gender *" error={errors.gender_id}>
                  <select style={{ ...S.input, ...(errors.gender_id ? S.inputErr : {}) }} value={form.gender_id} onChange={(e) => set("gender_id", e.target.value)}>
                    <option value="">Select gender</option>
                    {visibleGenders.map((g) => <option key={g.id} value={g.id}>{g.name || g.display_label}</option>)}
                  </select>
                </Field>

                <Field label="Date of Birth *" error={errors.dob}>
                  <input type="date" style={{ ...S.input, ...(errors.dob ? S.inputErr : {}) }} value={form.birth_date || form.dob} onChange={(e) => set("birth_date", e.target.value)} />
                </Field>

                <Field label="Birth Time (Optional)">
                  <TimeInput12Hour value={form.birth_time} onChange={(value) => set("birth_time", value)} inputStyle={S.input} />
                </Field>

                <Field label="Birth Place Location" span2>
                  <LocationPicker
                    lookups={lookups}
                    values={{
                      country: form.birth_country,
                      state_id: form.birth_state_id,
                      district_id: form.birth_district_id,
                      tehsil_id: form.birth_tehsil_id,
                      city_id: form.birth_place_city_id || form.birth_place_id,
                    }}
                    onChange={{
                      country: (v) => set("birth_country", v),
                      state_id: (v) => set("birth_state_id", v),
                      district_id: (v) => set("birth_district_id", v),
                      tehsil_id: (v) => set("birth_tehsil_id", v),
                      city_id: (v) => set("birth_place_city_id", v),
                    }}
                  />
                </Field>

                <Field label="Birth Coordinates" span2>
                  <BirthLocationMapField
                    address={form.birth_place}
                    latitude={form.birth_latitude}
                    longitude={form.birth_longitude}
                    onAddressChange={(value) => set("birth_place", value)}
                    onLatitudeChange={(value) => set("birth_latitude", value)}
                    onLongitudeChange={(value) => set("birth_longitude", value)}
                  />
                </Field>

                <Field label="Caste">
                  <SearchableSelect
                    value={form.caste_id}
                    options={lookups.castes.map((c) => ({ id: c.id, label: c.name }))}
                    onChange={(value) => set("caste_id", value)}
                    placeholder="Select caste"
                    fieldShellStyle={S.input}
                    inputStyle={S.searchableSelectInput}
                    chevronButtonStyle={S.searchableSelectChevronButton}
                    menuStyle={S.searchableSelectMenu}
                    optionStyle={S.searchableSelectOption}
                    optionActiveStyle={S.searchableSelectOptionActive}
                  />
                </Field>

                <Field label="Sakha" span2>
                  <SakhaAssignmentsField
                    options={filteredSakhas.map((s) => ({ id: s.id, label: s.name }))}
                    values={{
                      sakha_id: form.sakha_id,
                      dadisa_sakha_id: form.dadisa_sakha_id,
                      nanisa_sakha_id: form.nanisa_sakha_id,
                      mamasa_sakha_id: form.mamasa_sakha_id,
                    }}
                    onChange={(field, value) => set(field, value)}
                    disabled={!form.caste_id}
                  />
                </Field>

                <Field label="Sampraday">
                  <SearchableSelect
                    value={form.sampraday_id}
                    options={lookups.sampradays.map((sampraday) => ({ id: sampraday.id, label: sampraday.name }))}
                    onChange={(value) => set("sampraday_id", value)}
                    placeholder="Select sampraday"
                    fieldShellStyle={S.input}
                    inputStyle={S.searchableSelectInput}
                    chevronButtonStyle={S.searchableSelectChevronButton}
                    menuStyle={S.searchableSelectMenu}
                    optionStyle={S.searchableSelectOption}
                    optionActiveStyle={S.searchableSelectOptionActive}
                  />
                </Field>

                <Field label="Sampraday Details" span2>
                  <textarea style={{ ...S.input, ...S.textarea }} value={form.sampraday_details} onChange={(e) => set("sampraday_details", e.target.value)} placeholder="Additional sampraday details, branch, or notes" />
                </Field>

                <Field label="Referral Source">
                  <SearchableSelect
                    value={form.referral_source_id}
                    options={safeArray(lookups.referralSources).map((item) => ({ id: item.id, label: item.name }))}
                    onChange={(value) => set("referral_source_id", value)}
                    onEmptyAction={createReferralSource}
                    emptyActionLabel={(query) =>
                      creatingReferralSource ? "Adding referral source..." : `Add "${query}" as non-trusted`
                    }
                    emptyActionHint="New referral sources added from this form are saved as non-trusted."
                    emptyActionDisabled={creatingReferralSource}
                    placeholder="Select referral source"
                    fieldShellStyle={S.input}
                    inputStyle={S.searchableSelectInput}
                    chevronButtonStyle={S.searchableSelectChevronButton}
                    menuStyle={S.searchableSelectMenu}
                    optionStyle={S.searchableSelectOption}
                    optionActiveStyle={S.searchableSelectOptionActive}
                  />
                  <div style={S.fieldHint}>If it is not listed, search by name and add it as a non-trusted referral source.</div>
                </Field>

                <Field label="About Me" span2>
                  <textarea style={{ ...S.input, ...S.textarea }} value={form.about_me} onChange={(e) => set("about_me", e.target.value)} placeholder="A short, warm introduction..." />
                </Field>
              </div>
            </StepShell>
          )}

          {step === ADDRESS_CONTACTS_STEP_INDEX && (
            <StepShell title="Address & Contacts" progress={progressPct}>
              <div style={S.grid2} className="jk-grid-2">
                <Field label="Profile Addresses" span2>
                  <div>
                    <SectionHead title="Addresses" onAdd={addAddress} small />
                    {form.addresses.map((address, index) => (
                      <div key={index} style={S.subCard}>
                        {form.addresses.length > 1 && <RemoveBtn onClick={() => removeAddress(index)} />}
                        <div style={S.grid2} className="jk-grid-2">
                          <Field label="Address Type">
                            <select style={S.input} value={address.address_type} onChange={(e) => setAddress(index, "address_type", e.target.value)}>
                              <option value="current">Current</option>
                              <option value="native">Native</option>
                              <option value="family">Family</option>
                              <option value="office">Office</option>
                            </select>
                          </Field>

                          <Field label="Address Line 1" span2>
                            <input style={S.input} value={address.address_line1} onChange={(e) => setAddress(index, "address_line1", e.target.value)} placeholder="House / Society / Street" />
                          </Field>

                          <Field label="Address Line 2" span2>
                            <input style={S.input} value={address.address_line2} onChange={(e) => setAddress(index, "address_line2", e.target.value)} placeholder="Area / Landmark" />
                          </Field>

                          <AddressLocationFields
                            lookups={lookups}
                            values={address}
                            onChange={{
                              country_id: (v) => setAddress(index, "country_id", v),
                              state_id: (v) => setAddress(index, "state_id", v),
                              district_id: (v) => setAddress(index, "district_id", v),
                              tehsil_id: (v) => setAddress(index, "tehsil_id", v),
                              visa_status_id: (v) => setAddress(index, "visa_status_id", v),
                              visa_name_id: (v) => setAddress(index, "visa_name_id", v),
                            }}
                          />

                          <Field label="Pincode">
                            <input style={S.input} value={address.pincode} onChange={(e) => setAddress(index, "pincode", e.target.value)} placeholder="411001" />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>

                <Field label="Contacts" span2>
                  <div>
                    <SectionHead title="Contact Numbers & Email" onAdd={addContact} small />
                    {errors.contacts && <div style={S.errorBanner}>{errors.contacts}</div>}
                    {(form.contacts || []).length === 0 && (
                      <p style={S.emptyHint}>No contacts added.</p>
                    )}

                    {(form.contacts || []).map((contact, index) => (
                      <div key={index} style={S.subCard}>
                        <RemoveBtn onClick={() => removeContact(index)} />

                        <div style={S.grid2} className="jk-grid-2">
                          <Field label="Type">
                            <select style={S.input} value={contact.contact_type} onChange={(e) => setContact(index, "contact_type", e.target.value)}>
                              <option value="mobile">Mobile</option>
                              <option value="whatsapp">WhatsApp</option>
                              <option value="email">Email</option>
                            </select>
                          </Field>

                          <Field label={getContactValueFieldLabel(contact.contact_type)}>
                            {isIndianPhoneContactType(contact.contact_type) ? (
                              <IndianPhoneInput
                                value={contact.contact_value}
                                onChange={(nextValue) => setContact(index, "contact_value", nextValue)}
                                inputStyle={S.input}
                                selectStyle={S.input}
                              />
                            ) : (
                              <input style={S.input} value={contact.contact_value} onChange={(e) => setContact(index, "contact_value", e.target.value)} />
                            )}
                          </Field>

                          <Field label="Contact Person">
                            <input style={S.input} value={getSelfContactName(form)} readOnly />
                          </Field>

                          <Field label="Relation">
                            <input style={S.input} value="self" readOnly />
                          </Field>

                          <Field label="Primary">
                            <select style={S.input} value={contact.is_primary ? "yes" : "no"} onChange={(e) => setContact(index, "is_primary", e.target.value === "yes")}>
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </Field>

                          <Field label="Verified">
                            <select style={S.input} value={contact.is_verified ? "yes" : "no"} onChange={(e) => setContact(index, "is_verified", e.target.value === "yes")}>
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>
            </StepShell>
          )}

          {step === 3 && (
            <StepShell title="Personal Details" progress={progressPct}>
              <div style={S.grid2} className="jk-grid-2">
                <Field label="Marital Status">
                  <select style={S.input} value={form.marital_status_id} onChange={(e) => set("marital_status_id", e.target.value)}>
                    <option value="">Select</option>
                    {lookups.maritalStatus.map((m) => <option key={m.id} value={m.id}>{formatDropdownLabel(m.name || m.display_label)}</option>)}
                  </select>
                </Field>

                {shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatus) && (
                  <Field label="Has Children">
                    <select style={S.input} value={form.has_children ? "yes" : "no"} onChange={(e) => set("has_children", e.target.value === "yes")}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                )}

                {!shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatus) && (
                  <Field label="Height">
                    <HeightInputField
                      valueCm={form.height_cm || form.height}
                      onChangeCm={(value) => set("height_cm", value)}
                    />
                  </Field>
                )}

                {shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatus) && (
                  <Field label="Marital Field Details" span2>
                    <textarea
                      style={{ ...S.input, ...S.textarea }}
                      value={form.marital_field_details}
                      onChange={(e) => set("marital_field_details", e.target.value)}
                      placeholder="Mention children, previous marriage details, custody, or other relevant notes"
                    />
                  </Field>
                )}

                {shouldShowChildrenSection(form.marital_status_id, lookups.maritalStatus) && (
                  <Field label="Height">
                    <HeightInputField
                      valueCm={form.height_cm || form.height}
                      onChangeCm={(value) => set("height_cm", value)}
                    />
                  </Field>
                )}

                <Field label="Complexion">
                  <select style={S.input} value={form.complexion_id} onChange={(e) => set("complexion_id", e.target.value)}>
                    <option value="">Select</option>
                    {lookups.complexions.map((c) => <option key={c.id} value={c.id}>{c.name || c.display_label}</option>)}
                  </select>
                </Field>

                <Field label="Blood Group">
                  <select style={S.input} value={form.blood_group_id} onChange={(e) => set("blood_group_id", e.target.value)}>
                    <option value="">Select</option>
                    {lookups.bloodGroups.map((b) => <option key={b.id} value={b.id}>{b.name || b.display_label}</option>)}
                  </select>
                </Field>

                <Field label="Manglik">
                  <select style={S.input} value={form.manglik} onChange={(e) => set("manglik", e.target.value)}>
                    <option value="Do Not Know">Do Not Know</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Anshik">Anshik</option>
                  </select>
                </Field>

                <Field label="Disability">
                  <select style={S.input} value={form.has_disability ? "yes" : "no"} onChange={(e) => set("has_disability", e.target.value === "yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </Field>

                {form.has_disability && (
                  <Field label="Disability Description" span2>
                    <textarea style={{ ...S.input, ...S.textarea }} value={form.disability_description} onChange={(e) => set("disability_description", e.target.value)} />
                  </Field>
                )}
              </div>
            </StepShell>
          )}

          {step === 4 && (
            <StepShell title="Education & Career" progress={progressPct}>
              <SectionHead title="Education" onAdd={addEducation} />

              {form.educations.map((edu, index) => (
                <div key={index} style={S.subCard}>
                  {form.educations.length > 1 && <RemoveBtn onClick={() => removeEducation(index)} />}
                  <div style={S.grid2} className="jk-grid-2">
                    <Field label="Education Category">
                      <select style={S.input} value={edu.education_category_id} onChange={(e) => setEducation(index, "education_category_id", e.target.value)}>
                        <option value="">Select</option>
                        {lookups.educationCategories.map((ec) => <option key={ec.id} value={ec.id}>{ec.name}</option>)}
                      </select>
                    </Field>

                    <Field label="Degree Name">
                      <select
                        style={S.input}
                        value={edu.degree_name}
                        onChange={(e) => setEducation(index, "degree_name", e.target.value)}
                        disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                      >
                        <option value="">
                          {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                            ? "Select education category first"
                            : "Select degree"}
                        </option>
                        {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                          <option key={degree.value} value={degree.value}>{degree.label}</option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Description">
                      <input style={S.input} value={edu.education_description} onChange={(e) => setEducation(index, "education_description", e.target.value)} placeholder="Chartered Accountant" />
                    </Field>

                    <Field label="Institution">
                      <input style={S.input} value={edu.institution_name} onChange={(e) => setEducation(index, "institution_name", e.target.value)} placeholder="Pune University" />
                    </Field>

                    <Field label="Education Location">
                      <LocationAutocompleteField
                        value={edu.education_city_id}
                        locations={lookups.locations}
                        inputStyle={S.input}
                        placeholder="Search education location"
                        getOptionLabel={getLocationCityName}
                        onChange={(selectedCityId) => setEducation(index, "education_city_id", selectedCityId)}
                      />
                    </Field>

                    <Field label="Pursuing">
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 48, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={(edu.education_status || "completed") === "pursuing"}
                          onChange={(e) => setEducation(index, "education_status", e.target.checked ? "pursuing" : "completed")}
                        />
                        <span>Pursuing this degree</span>
                      </label>
                    </Field>

                    <Field label="Passing / Expected Year">
                      <input type="number" style={S.input} value={edu.passing_year} onChange={(e) => setEducation(index, "passing_year", e.target.value)} placeholder="2022" />
                    </Field>

                    <Field label="Highest Degree">
                      <select
                        style={S.input}
                        value={edu.is_highest_degree ? "yes" : "no"}
                        onChange={(e) => {
                          const isHighest = e.target.value === "yes";
                          setForm((prev) => ({
                            ...prev,
                            educations: prev.educations.map((x, i) => ({
                              ...x,
                              is_highest_degree: i === index ? isHighest : isHighest ? false : x.is_highest_degree,
                            })),
                          }));
                        }}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </Field>
                  </div>
                </div>
              ))}

              <div style={S.spacer} />

              <SectionHead title="Occupations" onAdd={addOccupation} />
              {errors.occupation && <div style={S.errorBanner}>{errors.occupation}</div>}

              {(form.occupations || []).map((occupation, index) => {
                const isServiceOccupation = isServiceOccupationTypeLabel(
                  getLookupName(lookups.occupationTypes, occupation.occupation_type_id) ||
                  occupation.occupation_type
                );

                return (
                  <div key={index} style={S.subCard}>
                    {form.occupations.length > 1 && <RemoveBtn onClick={() => removeOccupation(index)} />}
                    <div style={{ color: "#5f6776", fontSize: 12, marginBottom: 12 }}>
                      {index === 0 ? "Primary occupation" : `Additional occupation ${index + 1}`}
                    </div>
                    <div style={S.grid2} className="jk-grid-2">
                      <Field label="Occupation Type">
                        <select style={S.input} value={occupation.occupation_type_id} onChange={(e) => setOccupation(index, "occupation_type_id", e.target.value)}>
                          <option value="">Select occupation type</option>
                          {lookups.occupationTypes.map((occupationType) => (
                            <option key={occupationType.id} value={occupationType.id}>
                              {occupationType.name || occupationType.display_label}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Company">
                        <input style={S.input} value={occupation.company_name} onChange={(e) => setOccupation(index, "company_name", e.target.value)} placeholder="Sanghvi Textiles" />
                      </Field>

                      <Field label="Occupation Details">
                        <input style={S.input} value={occupation.occupation_details} onChange={(e) => setOccupation(index, "occupation_details", e.target.value)} placeholder="Bank Officer" />
                      </Field>

                      <Field label="Work Profile">
                        <input style={S.input} value={occupation.work_profile} onChange={(e) => setOccupation(index, "work_profile", e.target.value)} placeholder="Customer service and branch operations" />
                      </Field>

                      {isServiceOccupation ? (
                        <Field label="Annual Income">
                          <CurrencyAmountInput
                            amount={occupation.annual_income_amount || ""}
                            currencyCode={occupation.annual_income_currency_code || "INR"}
                            onAmountChange={(value) => setOccupation(index, "annual_income_amount", value)}
                            onCurrencyChange={(value) => setOccupation(index, "annual_income_currency_code", value)}
                            placeholder="1200000"
                          />
                        </Field>
                      ) : (
                        <Field label="Annual Turnover">
                          <CurrencyAmountInput
                            amount={occupation.annual_turnover_amount || ""}
                            currencyCode={occupation.annual_turnover_currency_code || "INR"}
                            onAmountChange={(value) => setOccupation(index, "annual_turnover_amount", value)}
                            onCurrencyChange={(value) => setOccupation(index, "annual_turnover_currency_code", value)}
                            placeholder="2500000"
                          />
                        </Field>
                      )}
                    </div>
                  </div>
                );
              })}
            </StepShell>
          )}

          {step === FAMILY_STEP_INDEX && (
            <StepShell title="Family" progress={progressPct}>
              <SectionHead title="Family" onAdd={addFamilyMember} />
              {errors.relatives && <div style={S.errorBanner}>{errors.relatives}</div>}
              {familyEntries.length === 0 && <p style={S.emptyHint}>No family members added.</p>}

              {familyEntries.map(({ relative, index }) => (
                <div key={index} style={S.subCard}>
                  {familyEntries.length > 1 && <RemoveBtn onClick={() => removeRelative(index)} />}

                  <div style={S.grid2} className="jk-grid-2">
                    <Field label="Relation Type">
                      <select style={S.input} value={relative.relation_type_id || ""} onChange={(e) => setRelative(index, "relation_type_id", e.target.value)}>
                        <option value="">Select relation type</option>
                        {familyRelationOptions.map((relationType) => (
                          <option key={relationType.id} value={relationType.id}>
                            {formatDropdownLabel(relationType.name || relationType.display_label)}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Gender">
                      <select style={S.input} value={relative.gender} onChange={(e) => setRelative(index, "gender", e.target.value)}>
                        <option value="">Select</option>
                        {visibleGenders.map((g) => <option key={g.id} value={g.name || g.display_label}>{g.name || g.display_label}</option>)}
                      </select>
                    </Field>

                    <Field label="First Name">
                      <input style={S.input} value={relative.first_name} onChange={(e) => setRelative(index, "first_name", e.target.value)} />
                    </Field>

                    <Field label="Middle Name">
                      <input style={S.input} value={relative.middle_name} onChange={(e) => setRelative(index, "middle_name", e.target.value)} />
                    </Field>

                    <Field label="Last Name">
                      <input style={S.input} value={relative.last_name} onChange={(e) => setRelative(index, "last_name", e.target.value)} />
                    </Field>

                    <Field label="Family Contacts" span2>
                      <div style={S.relativeEduBox}>
                        <SectionHead title="Contact Numbers & Email" onAdd={() => addRelativeContact(index)} small />
                        {safeArray(relative.contacts).length === 0 && <p style={S.emptyHint}>No contacts added.</p>}
                        {safeArray(relative.contacts).map((contact, contactIndex) => (
                          <div key={contactIndex} style={S.miniCard}>
                            <RemoveBtn onClick={() => removeRelativeContact(index, contactIndex)} />
                            <div style={S.grid2} className="jk-grid-2">
                              <Field label="Type">
                                <select style={S.input} value={contact.contact_type || "mobile"} onChange={(e) => setRelativeContact(index, contactIndex, "contact_type", e.target.value)}>
                                  <option value="mobile">Mobile</option>
                                  <option value="whatsapp">WhatsApp</option>
                                  <option value="parent_mobile">Parent Mobile</option>
                                  <option value="email">Email</option>
                                  <option value="other">Other</option>
                                </select>
                              </Field>
                              <Field label={getContactValueFieldLabel(contact.contact_type)}>
                                {isIndianPhoneContactType(contact.contact_type) ? (
                                  <IndianPhoneInput
                                    value={contact.contact_value || ""}
                                    onChange={(nextValue) => setRelativeContact(index, contactIndex, "contact_value", nextValue)}
                                    inputStyle={S.input}
                                    selectStyle={S.input}
                                  />
                                ) : (
                                  <input style={S.input} value={contact.contact_value || ""} onChange={(e) => setRelativeContact(index, contactIndex, "contact_value", e.target.value)} />
                                )}
                              </Field>
                              <Field label="Contact Person">
                                <input style={S.input} value={getRelativeDisplayName(relative)} readOnly />
                              </Field>
                              <Field label="Relation">
                                <input style={S.input} value={getRelativeRelationLabel(relative, lookups.relationTypes)} readOnly />
                              </Field>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Field>

                    <Field label="Family Location" span2>
                      <LocationPicker
                        lookups={lookups}
                        values={{
                          country: relative.country,
                          state_id: relative.state_id,
                          district_id: relative.district_id,
                          tehsil_id: relative.tehsil_id,
                          city_id: relative.city_id,
                        }}
                        onChange={{
                          country: (v) => setRelative(index, "country", v),
                          state_id: (v) => setRelative(index, "state_id", v),
                          district_id: (v) => setRelative(index, "district_id", v),
                          tehsil_id: (v) => setRelative(index, "tehsil_id", v),
                          city_id: (v) => setRelative(index, "city_id", v),
                        }}
                      />
                    </Field>

                    <Field label="Family Sakha">
                      <select style={S.input} value={relative.sakha_id} onChange={(e) => setRelative(index, "sakha_id", e.target.value)}>
                        <option value="">Select sakha</option>
                        {filteredSakhas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </Field>

                    <Field label="Is Deceased?">
                      <select style={S.input} value={relative.is_deceased ? "yes" : "no"} onChange={(e) => setRelative(index, "is_deceased", e.target.value === "yes")}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </Field>

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && (
                      <Field label="Marital Status">
                        <select style={S.input} value={relative.marital_status || ""} onChange={(e) => setRelative(index, "marital_status", e.target.value)}>
                          <option value="">Select marital status</option>
                          <option value="unmarried">Unmarried</option>
                          <option value="married">Married</option>
                        </select>
                      </Field>
                    )}

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && (
                      <Field label="Future Prospect">
                        <select style={S.input} value={relative.is_future_prospect ? "yes" : "no"} onChange={(e) => setRelative(index, "is_future_prospect", e.target.value === "yes")}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </Field>
                    )}

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect && (
                      <Field label="Eligible Year">
                        <input
                          type="number"
                          style={S.input}
                          value={relative.future_prospect_eligible_year || ""}
                          onChange={(e) => setRelative(index, "future_prospect_eligible_year", e.target.value)}
                          placeholder="e.g. 2028"
                          min={1900}
                          max={9999}
                        />
                      </Field>
                    )}

                    <Field label="Occupation Type">
                      <select style={S.input} value={relative.occupation_type_id || ""} onChange={(e) => setRelative(index, "occupation_type_id", e.target.value)}>
                        <option value="">Select occupation type</option>
                        {lookups.occupationTypes.map((occupationType) => (
                          <option key={occupationType.id} value={occupationType.id}>
                            {occupationType.name || occupationType.display_label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Occupation Details">
                      <input style={S.input} value={relative.occupation_details} onChange={(e) => setRelative(index, "occupation_details", e.target.value)} />
                    </Field>
                  </div>

                  <div style={S.relativeEduBox}>
                    <SectionHead title="Family Education" onAdd={() => addRelativeEducation(index)} small />

                    {(relative.education || []).map((edu, eduIndex) => (
                      <div key={eduIndex} style={S.miniCard}>
                        {(relative.education || []).length > 1 && <RemoveBtn onClick={() => removeRelativeEducation(index, eduIndex)} />}

                        <div style={S.grid2} className="jk-grid-2">
                          <Field label="Category">
                            <select style={S.input} value={edu.education_category_id} onChange={(e) => setRelativeEducation(index, eduIndex, "education_category_id", e.target.value)}>
                              <option value="">Select</option>
                              {lookups.educationCategories.map((ec) => <option key={ec.id} value={ec.id}>{ec.name}</option>)}
                            </select>
                          </Field>

                          <Field label="Degree">
                            <select
                              style={S.input}
                              value={edu.degree_name}
                              onChange={(e) => setRelativeEducation(index, eduIndex, "degree_name", e.target.value)}
                              disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                            >
                              <option value="">
                                {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                                  ? "Select education category first"
                                  : "Select degree"}
                              </option>
                              {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                                <option key={degree.value} value={degree.value}>{degree.label}</option>
                              ))}
                            </select>
                          </Field>

                          <Field label="Institution">
                            <input style={S.input} value={edu.institution_name} onChange={(e) => setRelativeEducation(index, eduIndex, "institution_name", e.target.value)} />
                          </Field>

                          <Field label="Passing Year">
                            <input type="number" style={S.input} value={edu.passing_year} onChange={(e) => setRelativeEducation(index, eduIndex, "passing_year", e.target.value)} />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </StepShell>
          )}

          {step === RELATIVES_STEP_INDEX && (
            <StepShell title="Relatives" progress={progressPct}>
              <SectionHead title="Relatives" onAdd={addRelative} />
              {errors.relatives && <div style={S.errorBanner}>{errors.relatives}</div>}
              {sideRelativeEntries.length === 0 && <p style={S.emptyHint}>No relatives added.</p>}

              {sideRelativeEntries.map(({ relative, index }) => (
                <div key={index} style={S.subCard}>
                  <RemoveBtn onClick={() => removeRelative(index)} />

                  <div style={S.grid2} className="jk-grid-2">
                    <Field label="Relation Type">
                      <select style={S.input} value={relative.relation_type_id || ""} onChange={(e) => setRelative(index, "relation_type_id", e.target.value)}>
                        <option value="">Select relation type</option>
                        {sideRelativeRelationOptions.map((relationType) => (
                          <option key={relationType.id} value={relationType.id}>
                            {formatDropdownLabel(relationType.name || relationType.display_label)}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Family Side">
                      <select style={S.input} value={normalizeRelativeFamilySide(relative.family_side)} onChange={(e) => setRelative(index, "family_side", e.target.value)}>
                        <option value="paternal">Paternal</option>
                        <option value="maternal">Maternal</option>
                      </select>
                    </Field>

                    <Field label="Gender">
                      <select style={S.input} value={relative.gender} onChange={(e) => setRelative(index, "gender", e.target.value)}>
                        <option value="">Select</option>
                        {visibleGenders.map((g) => <option key={g.id} value={g.name || g.display_label}>{g.name || g.display_label}</option>)}
                      </select>
                    </Field>

                    <Field label="First Name">
                      <input style={S.input} value={relative.first_name} onChange={(e) => setRelative(index, "first_name", e.target.value)} />
                    </Field>

                    <Field label="Middle Name">
                      <input style={S.input} value={relative.middle_name} onChange={(e) => setRelative(index, "middle_name", e.target.value)} />
                    </Field>

                    <Field label="Last Name">
                      <input style={S.input} value={relative.last_name} onChange={(e) => setRelative(index, "last_name", e.target.value)} />
                    </Field>

                    <Field label="Relative Contacts" span2>
                      <div style={S.relativeEduBox}>
                        <SectionHead title="Contact Numbers & Email" onAdd={() => addRelativeContact(index)} small />
                        {safeArray(relative.contacts).length === 0 && <p style={S.emptyHint}>No contacts added.</p>}
                        {safeArray(relative.contacts).map((contact, contactIndex) => (
                          <div key={contactIndex} style={S.miniCard}>
                            <RemoveBtn onClick={() => removeRelativeContact(index, contactIndex)} />
                            <div style={S.grid2} className="jk-grid-2">
                              <Field label="Type">
                                <select style={S.input} value={contact.contact_type || "mobile"} onChange={(e) => setRelativeContact(index, contactIndex, "contact_type", e.target.value)}>
                                  <option value="mobile">Mobile</option>
                                  <option value="whatsapp">WhatsApp</option>
                                  <option value="parent_mobile">Parent Mobile</option>
                                  <option value="email">Email</option>
                                  <option value="other">Other</option>
                                </select>
                              </Field>
                              <Field label={getContactValueFieldLabel(contact.contact_type)}>
                                {isIndianPhoneContactType(contact.contact_type) ? (
                                  <IndianPhoneInput
                                    value={contact.contact_value || ""}
                                    onChange={(nextValue) => setRelativeContact(index, contactIndex, "contact_value", nextValue)}
                                    inputStyle={S.input}
                                    selectStyle={S.input}
                                  />
                                ) : (
                                  <input style={S.input} value={contact.contact_value || ""} onChange={(e) => setRelativeContact(index, contactIndex, "contact_value", e.target.value)} />
                                )}
                              </Field>
                              <Field label="Contact Person">
                                <input style={S.input} value={getRelativeDisplayName(relative)} readOnly />
                              </Field>
                              <Field label="Relation">
                                <input style={S.input} value={getRelativeRelationLabel(relative, lookups.relationTypes)} readOnly />
                              </Field>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Field>

                    <Field label="Relative Location" span2>
                      <LocationPicker
                        lookups={lookups}
                        values={{
                          country: relative.country,
                          state_id: relative.state_id,
                          district_id: relative.district_id,
                          tehsil_id: relative.tehsil_id,
                          city_id: relative.city_id,
                        }}
                        onChange={{
                          country: (v) => setRelative(index, "country", v),
                          state_id: (v) => setRelative(index, "state_id", v),
                          district_id: (v) => setRelative(index, "district_id", v),
                          tehsil_id: (v) => setRelative(index, "tehsil_id", v),
                          city_id: (v) => setRelative(index, "city_id", v),
                        }}
                      />
                    </Field>

                    <Field label="Relative Sakha">
                      <select style={S.input} value={relative.sakha_id} onChange={(e) => setRelative(index, "sakha_id", e.target.value)}>
                        <option value="">Select sakha</option>
                        {filteredSakhas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </Field>

                    <Field label="Is Deceased?">
                      <select style={S.input} value={relative.is_deceased ? "yes" : "no"} onChange={(e) => setRelative(index, "is_deceased", e.target.value === "yes")}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </Field>

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && (
                      <Field label="Marital Status">
                        <select style={S.input} value={relative.marital_status || ""} onChange={(e) => setRelative(index, "marital_status", e.target.value)}>
                          <option value="">Select marital status</option>
                          <option value="unmarried">Unmarried</option>
                          <option value="married">Married</option>
                        </select>
                      </Field>
                    )}

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && (
                      <Field label="Future Prospect">
                        <select style={S.input} value={relative.is_future_prospect ? "yes" : "no"} onChange={(e) => setRelative(index, "is_future_prospect", e.target.value === "yes")}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </Field>
                    )}

                    {shouldShowRelativeMaritalStatus(relative, lookups.relationTypes) && isRelativeUnmarriedStatus(relative.marital_status) && relative.is_future_prospect && (
                      <Field label="Eligible Year">
                        <input
                          type="number"
                          style={S.input}
                          value={relative.future_prospect_eligible_year || ""}
                          onChange={(e) => setRelative(index, "future_prospect_eligible_year", e.target.value)}
                          placeholder="e.g. 2028"
                          min={1900}
                          max={9999}
                        />
                      </Field>
                    )}

                    <Field label="Occupation Type">
                      <select style={S.input} value={relative.occupation_type_id || ""} onChange={(e) => setRelative(index, "occupation_type_id", e.target.value)}>
                        <option value="">Select occupation type</option>
                        {lookups.occupationTypes.map((occupationType) => (
                          <option key={occupationType.id} value={occupationType.id}>
                            {occupationType.name || occupationType.display_label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Occupation Details">
                      <input style={S.input} value={relative.occupation_details} onChange={(e) => setRelative(index, "occupation_details", e.target.value)} />
                    </Field>
                  </div>

                  <div style={S.relativeEduBox}>
                    <SectionHead title="Relative Education" onAdd={() => addRelativeEducation(index)} small />

                    {(relative.education || []).map((edu, eduIndex) => (
                      <div key={eduIndex} style={S.miniCard}>
                        {(relative.education || []).length > 1 && <RemoveBtn onClick={() => removeRelativeEducation(index, eduIndex)} />}

                        <div style={S.grid2} className="jk-grid-2">
                          <Field label="Category">
                            <select style={S.input} value={edu.education_category_id} onChange={(e) => setRelativeEducation(index, eduIndex, "education_category_id", e.target.value)}>
                              <option value="">Select</option>
                              {lookups.educationCategories.map((ec) => <option key={ec.id} value={ec.id}>{ec.name}</option>)}
                            </select>
                          </Field>

                          <Field label="Degree">
                            <select
                              style={S.input}
                              value={edu.degree_name}
                              onChange={(e) => setRelativeEducation(index, eduIndex, "degree_name", e.target.value)}
                              disabled={hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id}
                            >
                              <option value="">
                                {hasDegreeCategoryMapping(lookups.degreeNames) && !edu.education_category_id
                                  ? "Select education category first"
                                  : "Select degree"}
                              </option>
                              {buildDegreeNameOptions(lookups.degreeNames, edu.degree_name, edu.education_category_id).map((degree) => (
                                <option key={degree.value} value={degree.value}>{degree.label}</option>
                              ))}
                            </select>
                          </Field>

                          <Field label="Institution">
                            <input style={S.input} value={edu.institution_name} onChange={(e) => setRelativeEducation(index, eduIndex, "institution_name", e.target.value)} />
                          </Field>

                          <Field label="Passing Year">
                            <input type="number" style={S.input} value={edu.passing_year} onChange={(e) => setRelativeEducation(index, eduIndex, "passing_year", e.target.value)} />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </StepShell>
          )}

          {step === 7 && (
            <StepShell title="Partner Preferences" progress={progressPct}>
              <div style={S.grid2} className="jk-grid-2">
                <Field label="Preferred Age Difference" span2>
                  <div style={S.prefRangeGrid}>
                    <div style={S.signedInputWrap}>
                      <span style={S.signedInputPrefix}>-</span>
                      <div style={S.prefRangeInputRow}>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          style={{ ...S.input, ...S.signedInput }}
                          value={form.preferences.preferred_age_min}
                          onChange={(e) => setNested("preferences", "preferred_age_min", e.target.value)}
                          placeholder="Value"
                        />
                        <select
                          style={S.input}
                          value={form.preferences.preferred_age_min_unit || "months"}
                          onChange={(e) => setNested("preferences", "preferred_age_min_unit", e.target.value)}
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                    </div>
                    <div style={S.signedInputWrap}>
                      <span style={S.signedInputPrefix}>+</span>
                      <div style={S.prefRangeInputRow}>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          style={{ ...S.input, ...S.signedInput }}
                          value={form.preferences.preferred_age_max}
                          onChange={(e) => setNested("preferences", "preferred_age_max", e.target.value)}
                          placeholder="Value"
                        />
                        <select
                          style={S.input}
                          value={form.preferences.preferred_age_max_unit || "months"}
                          onChange={(e) => setNested("preferences", "preferred_age_max_unit", e.target.value)}
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </Field>

                <Field label="Preferred Cities" span2>
                  <CityPreferenceDropdown
                    placeholder="Select preferred cities"
                    options={preferredCityOptions}
                    tierOptions={lookups.cityTiers}
                    selected={form.preferences.preferred_city_ids}
                    onToggle={(value) => togglePrefValue("preferred_city_ids", Number(value))}
                  />
                </Field>

                <Field label="Non Preferred Cities" span2>
                  <CityPreferenceDropdown
                    placeholder="Select non preferred cities"
                    options={preferredCityOptions}
                    tierOptions={lookups.cityTiers}
                    selected={form.preferences.non_preferred_city_ids}
                    onToggle={(value) => togglePrefValue("non_preferred_city_ids", Number(value))}
                  />
                </Field>

                <Field label="Non Preferred Sakhas" span2>
                  <MultiSelectDropdown
                    placeholder="Select non preferred sakhas"
                    options={preferredSakhaOptions}
                    selected={form.preferences.non_preferred_sakha_ids}
                    onToggle={(value) => togglePrefValue("non_preferred_sakha_ids", Number(value))}
                  />
                </Field>

                <Field label="Preferred Education Categories" span2>
                  <MultiSelectDropdown
                    placeholder="Select preferred education categories"
                    options={preferredEducationOptions}
                    selected={form.preferences.preferred_education_category_ids}
                    onToggle={(value) => togglePrefValue("preferred_education_category_ids", Number(value))}
                  />
                </Field>

                <Field label="Non Preferred Education Categories" span2>
                  <MultiSelectDropdown
                    placeholder="Select non preferred education categories"
                    options={preferredEducationOptions}
                    selected={form.preferences.non_preferred_education_category_ids}
                    onToggle={(value) => togglePrefValue("non_preferred_education_category_ids", Number(value))}
                  />
                </Field>

                <Field label="Preferred Degrees" span2>
                  <MultiSelectDropdown
                    placeholder="Select preferred degrees"
                    options={preferredDegreeOptions}
                    selected={form.preferences.preferred_degrees}
                    onToggle={(value) => togglePrefValue("preferred_degrees", value)}
                  />
                </Field>

                <Field label="Non Preferred Degrees" span2>
                  <MultiSelectDropdown
                    placeholder="Select non preferred degrees"
                    options={preferredDegreeOptions}
                    selected={form.preferences.non_preferred_degrees}
                    onToggle={(value) => togglePrefValue("non_preferred_degrees", value)}
                  />
                </Field>

                <Field label="Preferred Occupation Types" span2>
                  <MultiSelectDropdown
                    placeholder="Select preferred occupations"
                    options={preferredOccupationOptions}
                    selected={form.preferences.preferred_occupation_types}
                    onToggle={(value) => togglePrefValue("preferred_occupation_types", value)}
                  />
                </Field>

                <Field label="Non Preferred Occupation Types" span2>
                  <MultiSelectDropdown
                    placeholder="Select non preferred occupations"
                    options={preferredOccupationOptions}
                    selected={form.preferences.non_preferred_occupation_types}
                    onToggle={(value) => togglePrefValue("non_preferred_occupation_types", value)}
                  />
                </Field>

                <Field label="Preference Notes" span2>
                  <textarea style={{ ...S.input, ...S.textarea }} value={form.preferences.notes} onChange={(e) => setNested("preferences", "notes", e.target.value)} />
                </Field>
              </div>
            </StepShell>
          )}

          {step === 8 && (
            <StepShell title="Tags & Media" progress={progressPct}>
              <div style={S.mediaStack}>
                <div style={S.subCard}>
                  <p style={S.sectionTitle}>Tags</p>
                  <p style={S.photoHint}>Use tags to make the profile easier to search and shortlist later.</p>
                  <MultiSelectDropdown
                    placeholder="Search or create tags"
                    options={tagOptions}
                    selected={form.tags}
                    onToggle={(value) => toggleTag(value)}
                    onCreate={createTag}
                    createPrefix="Create tag"
                  />
                </div>

              <p style={S.mediaLead}>Finish the profile with quick tags, photos, and supporting documents so the profile is ready for review and search.</p>

              <div style={S.subCard}>
              <p style={S.sectionTitle}>Profile Photos</p>
              <p style={S.photoHint}>Upload up to 10 photos, JPEG, PNG, WEBP, max {MAX_UPLOAD_FILE_SIZE_LABEL} each after compression, click a photo to set primary.</p>

              <div
                style={{ ...S.dropZone, ...(preparingPhotoFiles ? S.processingDropZone : {}) }}
                onClick={() => {
                  if (!preparingPhotoFiles) fileInputRef.current?.click();
                }}
              >
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} onChange={handleFileSelect} disabled={preparingPhotoFiles} />
                <span style={S.dropIcon}>+</span>
                <span style={S.dropText}>{preparingPhotoFiles ? "Preparing photos..." : "Select profile photos"}</span>
                <span style={S.dropSub}>{imageFiles.length}/10 selected</span>
                {preparingPhotoFiles && (
                  <span style={S.processingInlineNote}>
                    <span style={S.processingSpinner} />
                    {photoPreparationStatus || "Compressing selected photos..."}
                  </span>
                )}
              </div>

              {imageFiles.length > 0 && (
                <div style={S.previewGrid}>
                  {imageFiles.map((img, index) => (
                    <div
                      key={index}
                      style={{ ...S.previewCard, ...(index === primaryIndex ? S.previewCardPrimary : {}) }}
                      onClick={() => setPrimaryIndex(index)}
                    >
                      <img src={img.previewUrl} alt={img.name} style={S.previewImg} />
                      {index === primaryIndex && <div style={S.primaryBadge}>Primary</div>}
                      <button
                        type="button"
                        style={S.previewRemove}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(index);
                        }}
                      >
                        x
                      </button>
                      <div style={S.previewName}>{img.name}</div>
                    </div>
                  ))}
                </div>
              )}

              </div>

              <div style={S.subCard}>
              <p style={S.sectionTitle}>Documents</p>
              <p style={S.photoHint}>Upload documents or images to AWS S3. All selected files will use the chosen document type.</p>

              <div style={S.grid2} className="jk-grid-2">
                <Field label="Document Type">
                  <select style={S.input} value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)}>
                    <option value="">Select document type</option>
                    {lookups.documentTypes.map((docType) => (
                      <option key={docType.id} value={docType.id}>{docType.name}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div
                style={{ ...S.dropZone, ...(preparingDocumentFiles ? S.processingDropZone : {}) }}
                onClick={() => {
                  if (!preparingDocumentFiles) documentInputRef.current?.click();
                }}
              >
                <input
                  ref={documentInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,image/jpeg,image/png"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleDocumentSelect}
                  disabled={preparingDocumentFiles}
                />
                <span style={S.dropIcon}>+</span>
                <span style={S.dropText}>{preparingDocumentFiles ? "Preparing documents..." : "Select documents"}</span>
                <span style={S.dropSub}>PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG, PNG, large files are optimized during upload, {documentFiles.length}/10 selected</span>
                {preparingDocumentFiles && (
                  <span style={S.processingInlineNote}>
                    <span style={S.processingSpinner} />
                    {documentPreparationStatus || "Compressing selected documents..."}
                  </span>
                )}
              </div>

              {documentFiles.length > 0 && (
                <div style={S.docList}>
                  {documentFiles.map((doc, index) => (
                    <div key={`${doc.name}-${index}`} style={S.docItem}>
                      <div>
                        <div style={S.docName}>{doc.name}</div>
                        <div style={S.docMeta}>{formatFileSize(doc.size)} - {doc.type || "document"}</div>
                      </div>
                      <button type="button" style={S.docRemove} onClick={() => removeDocument(index)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
              </div>
              </div>

            </StepShell>
          )}
        </div>

        {errors.duplicate && <div style={S.errorBanner}>{errors.duplicate}</div>}
        {errors.profile_save && <div style={S.errorBanner}>{errors.profile_save}</div>}

        <div style={S.nav}>
          <button style={{ ...S.navBtn, ...S.navBack }} disabled={!canPrev} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            {"< Back"}
          </button>

          <span style={S.navCounter}>{step + 1} / {STEPS.length}</span>

          {step === DUPLICATE_STEP_INDEX ? (
            <button
              style={{
                ...S.navBtn,
                ...S.navSubmit,
                ...((!canProceedFromDuplicate || duplicateChecking || loading) ? S.navBtnDisabled : {}),
              }}
              onClick={handleDuplicateStepCreate}
              disabled={!canProceedFromDuplicate || duplicateChecking || loading}
            >
              {loading ? uploadProgress || "Creating..." : profileCreated ? "Continue" : "Create Profile & Go"}
            </button>
          ) : isLast ? (
            <button style={{ ...S.navBtn, ...S.navSubmit, ...(isPreparingSelectedFiles ? S.navBtnDisabled : {}) }} onClick={handleSubmit} disabled={loading || isPreparingSelectedFiles}>
              {loading ? uploadProgress || "Saving..." : isPreparingSelectedFiles ? "Preparing files..." : "Save & Finish"}
            </button>
          ) : (
            <button
              style={{
                ...S.navBtn,
                ...S.navNext,
                ...(isPreparingSelectedFiles ? S.navBtnDisabled : {}),
              }}
              onClick={goNext}
              disabled={isPreparingSelectedFiles}
            >
              {"Next >"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DuplicateCheckGate({
  form,
  set,
  errors,
  lookups,
  duplicateChecking,
  duplicateChecked,
  duplicateMatches,
  duplicateAction,
  setDuplicateAction,
  duplicateTargetId,
  setDuplicateTargetId,
  duplicateConfirmed,
  setDuplicateConfirmed,
  handleDuplicateCheck,
  onViewProfile,
}) {
  const phonetic = simpleProfilePhoneticKey(
    form.first_name,
    form.middle_name,
    form.last_name,
    [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(" ")
  );
  const visibleGenders = safeArray(lookups.genders).filter(
    (g) => String(g?.name || g?.display_label || "").trim().toLowerCase() !== "other"
  );

  return (
    <div style={S.resolveShell}>
      <div style={S.dupHeader}>
        <div>
          <h2 style={S.dupTitle}>Check duplicate before opening the full profile flow</h2>
          <p style={S.dupSub}>
            First, middle, and last name are required before duplicate check. Gender and date of birth are required. Birth time is optional and only affects duplicate matching when you enter it.
          </p>
        </div>

        <div style={S.phoneticCard}>
          <span style={S.phoneticLabel}>Phonetic Key</span>
          <b>{phonetic || "-"}</b>
        </div>
      </div>

      <div style={S.dupActions}>
        <button type="button" style={S.checkBtn} onClick={handleDuplicateCheck} disabled={duplicateChecking}>
          {duplicateChecking ? "Checking..." : "Run Duplicate Check"}
        </button>

        {duplicateChecked && duplicateMatches.length === 0 && <span style={S.safePill}>OK No duplicate found</span>}
      </div>

      <div style={{ ...S.grid2, padding: "0 22px 22px" }} className="jk-grid-2">
        <Field label="Gender *" error={errors.gender_id}>
          <select style={{ ...S.input, ...(errors.gender_id ? S.inputErr : {}) }} value={form.gender_id} onChange={(e) => set("gender_id", e.target.value)}>
            <option value="">Select gender</option>
            {visibleGenders.map((g) => <option key={g.id} value={g.id}>{g.name || g.display_label}</option>)}
          </select>
        </Field>

        <Field label="Date of Birth *" error={errors.dob}>
          <input type="date" style={{ ...S.input, ...(errors.dob ? S.inputErr : {}) }} value={form.birth_date || form.dob} onChange={(e) => set("birth_date", e.target.value)} />
        </Field>

        <Field label="First Name *" error={errors.first_name}>
          <input
            style={{ ...S.input, ...(errors.first_name ? S.inputErr : {}) }}
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
          />
        </Field>

        <Field label="Middle Name *" error={errors.middle_name}>
          <input
            style={{ ...S.input, ...(errors.middle_name ? S.inputErr : {}) }}
            value={form.middle_name}
            onChange={(e) => set("middle_name", e.target.value)}
          />
        </Field>

        <Field label="Last Name *" error={errors.last_name}>
          <input
            style={{ ...S.input, ...(errors.last_name ? S.inputErr : {}) }}
            value={form.last_name}
            onChange={(e) => set("last_name", e.target.value)}
          />
        </Field>

        <Field label="Birth Time (Optional)" error={errors.birth_time}>
          <TimeInput12Hour
            value={form.birth_time}
            onChange={(value) => set("birth_time", value)}
            inputStyle={{ ...S.input, ...(errors.birth_time ? S.inputErr : {}) }}
          />
        </Field>

      </div>

      <div style={S.dupActionBar}>
        <button type="button" style={S.checkBtn} onClick={handleDuplicateCheck} disabled={duplicateChecking}>
          {duplicateChecking ? "Checking..." : "Run Duplicate Check"}
        </button>

        {duplicateChecked && duplicateMatches.length === 0 && <span style={S.safePill}>No duplicate found</span>}
      </div>

      {duplicateMatches.length > 0 && (
        <div style={S.duplicateResolveOnly}>
          <DuplicateResolutionPanel
            title="Duplicate profiles found"
            subtitle="Review the matching profiles here before continuing. Choose Retain, Merge, or Replace, then confirm your review."
            matches={duplicateMatches}
            duplicateAction={duplicateAction}
            setDuplicateAction={setDuplicateAction}
            duplicateTargetId={duplicateTargetId}
            setDuplicateTargetId={setDuplicateTargetId}
            duplicateConfirmed={duplicateConfirmed}
            setDuplicateConfirmed={setDuplicateConfirmed}
            onViewProfile={onViewProfile}
          />
        </div>
      )}
    </div>
  );
}

function DuplicateResolutionPanel({
  title = "Resolve before saving this profile",
  subtitle,
  matches,
  duplicateAction,
  setDuplicateAction,
  duplicateTargetId,
  setDuplicateTargetId,
  duplicateConfirmed,
  setDuplicateConfirmed,
  showActions = true,
  onViewProfile,
}) {
  const selectedMatch = matches.find((m) => String(m.candidate.id) === String(duplicateTargetId));
  const actionNeedsTarget = duplicateAction === "merge" || duplicateAction === "replace";

  const chooseAction = (action) => {
    setDuplicateAction(action);
    setDuplicateConfirmed(false);

    if (action === "retain") {
      setDuplicateTargetId("");
    } else if (!duplicateTargetId && matches[0]?.candidate?.id) {
      setDuplicateTargetId(matches[0].candidate.id);
    }
  };

  return (
    <div style={S.resolveShell}>
      <div style={S.resolveHero}>
        <div>
          <p style={S.resolveEyebrow}>Duplicate Case Found</p>
          <h2 style={S.resolveTitle}>{title}</h2>
          <p style={S.resolveSub}>
            {subtitle || "Select a target profile for Merge/Replace. Backend will save all matches into profile_duplicate_cases and write profile_merge_history for merge/replace actions."}
          </p>
        </div>
        <div style={S.resolveScoreBubble}>
          <b>{Math.round((matches[0]?.score || 0) * 100)}%</b>
          <span>top match</span>
        </div>
      </div>

      <div style={S.resolveLayout} className="jk-duplicate-resolve-layout">
        <div style={S.matchList}>
          <div style={S.matchListHeader}>
            <span>Potential Matches</span>
            <b>{matches.length}</b>
          </div>

          {matches.map((match, index) => {
            const candidate = match.candidate || {};
            const isSelected = String(candidate.id) === String(duplicateTargetId);
            const name =
              candidate.full_name ||
              [candidate.first_name, candidate.middle_name, candidate.last_name].filter(Boolean).join(" ") ||
              "Unnamed profile";

            return (
              <button
                type="button"
                key={candidate.id || index}
                style={{ ...S.matchCard, ...(isSelected ? S.matchCardActive : {}) }}
                onClick={() => {
                  setDuplicateTargetId(candidate.id);
                  if (!duplicateAction || duplicateAction === "retain") setDuplicateAction("merge");
                  setDuplicateConfirmed(false);
                }}
              >
                <div style={S.matchCardTop}>
                  <div>
                    <b style={S.matchName}>{name}</b>
                    <span style={S.matchMeta}>
                      Profile #{candidate.profile_no || "-"} - {candidate.gender || "-"} - {String(candidate.birth_date || candidate.dob || "DOB -").slice(0, 10)}
                    </span>
                  </div>
                  <div style={S.matchCardActions}>
                    <button
                      type="button"
                      style={S.viewProfileBtn}
                      onClick={(event) => {
                        event.stopPropagation();
                        onViewProfile?.(candidate.id);
                      }}
                    >
                      View Profile
                    </button>
                    <strong style={S.matchScore}>{Math.round(Number(match.score || 0) * 100)}%</strong>
                  </div>
                </div>

                <div style={S.matchLocation}>
                  {[candidate.city, candidate.state, candidate.location_display_cache].filter(Boolean).slice(0, 2).join(" - ") || "Location not available"}
                </div>

                <div style={S.reasonList}>
                  {(match.reasons || []).slice(0, 6).map((reason, reasonIndex) => (
                    <span key={reasonIndex} style={S.reasonChip}>{reason}</span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {showActions && (
        <div style={S.actionPanel}>
          <div style={S.actionPanelHeader}>
            <p style={S.sectionTitle}>Resolution Action</p>
            {selectedMatch ? (
              <span style={S.selectedTargetChip}>Target #{selectedMatch.candidate.profile_no || "-"}</span>
            ) : (
              <span style={S.neutralTargetChip}>No target selected</span>
            )}
          </div>

          <div style={S.duplicateActionGrid}>
            <DuplicateActionCard
              active={duplicateAction === "retain"}
              title="Retain as New"
              badge="Safe"
              text="Create this as a separate active profile. Cases stay pending for admin review."
              onClick={() => chooseAction("retain")}
            />
            <DuplicateActionCard
              active={duplicateAction === "merge"}
              title="Merge with Target"
              badge="Recommended"
              text="Existing selected profile stays canonical; new profile becomes merged profile."
              onClick={() => chooseAction("merge")}
            />
            <DuplicateActionCard
              active={duplicateAction === "replace"}
              title="Replace Existing"
              badge="Careful"
              text="New profile becomes canonical replacement; selected existing profile is merged."
              onClick={() => chooseAction("replace")}
            />
          </div>

          {actionNeedsTarget && !duplicateTargetId && (
            <div style={S.resolveError}>Select one matching profile as the target for this action.</div>
          )}

          <div style={S.flowPreview}>
            <p style={S.flowTitle}>What will happen</p>
            {duplicateAction === "retain" && (
              <ul style={S.flowList}>
                <li>New profile is saved as active.</li>
                <li>All matches are inserted into <b>profile_duplicate_cases</b>.</li>
                <li>No <b>profile_merge_history</b> row is created.</li>
              </ul>
            )}
            {duplicateAction === "merge" && (
              <ul style={S.flowList}>
                <li>Selected existing profile is the canonical profile.</li>
                <li>New profile is marked merged using <b>profiles.canonical_profile_id</b>.</li>
                <li>Selected case is stored in <b>profile_duplicate_cases</b>.</li>
                <li>Merge snapshot is stored in <b>profile_merge_history</b>.</li>
              </ul>
            )}
            {duplicateAction === "replace" && (
              <ul style={S.flowList}>
                <li>New profile becomes the canonical replacement.</li>
                <li>Selected existing profile is marked merged into the new profile.</li>
                <li>Case and history are saved for rollback/audit.</li>
              </ul>
            )}
            {!duplicateAction && <p style={S.flowEmpty}>Pick Retain, Merge or Replace to continue.</p>}
          </div>

          <label style={S.overrideBox}>
            <input
              type="checkbox"
              checked={duplicateConfirmed}
              disabled={!duplicateAction || (actionNeedsTarget && !duplicateTargetId)}
              onChange={(e) => setDuplicateConfirmed(e.target.checked)}
            />
            <span>I reviewed the duplicate evidence and confirm this resolution.</span>
          </label>
        </div>
        )}
      </div>
    </div>
  );
}

function StepShell({ title, progress, children }) {
  return (
    <div>
      <div style={S.stepTitleRow}>
        <p style={S.sectionTitle}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function DuplicateActionCard({ active, title, badge, text, onClick }) {
  return (
    <button type="button" style={{ ...S.duplicateActionCard, ...(active ? S.duplicateActionCardActive : {}) }} onClick={onClick}>
      <div style={S.actionCardTop}>
        <b>{title}</b>
        {badge && <span style={S.actionBadge}>{badge}</span>}
      </div>
      <span>{text}</span>
    </button>
  );
}

function MultiSelectDropdown({ placeholder, options, selected, onToggle, disabled = false, onCreate = null, createPrefix = "Create" }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const cleanQuery = query.trim();
  const normalizedQuery = cleanQuery.toLowerCase();
  const selectedSet = new Set(safeArray(selected).map((value) => String(value)));
  const selectedOptions = safeArray(options).filter((option) => selectedSet.has(String(option.id)));
  const summaryOptions = selectedOptions.slice(0, 2);
  const remainingSelectedCount = Math.max(0, selectedOptions.length - summaryOptions.length);
  const visibleOptions = safeArray(options).filter((option) =>
    String(option.label || "").toLowerCase().includes(normalizedQuery)
  );
  const hasExactMatch = safeArray(options).some((option) =>
    String(option.label || "").trim().toLowerCase() === normalizedQuery
  );
  const canCreate = Boolean(onCreate && cleanQuery && !hasExactMatch);

  return (
    <details style={{ ...S.multiDropdown, ...(disabled ? S.multiDropdownDisabled : {}) }}>
      <summary style={{ ...S.multiDropdownSummary, ...(disabled ? S.multiDropdownSummaryDisabled : {}) }}>
        <div style={S.multiDropdownSummaryText}>
          {selectedOptions.length === 0 ? (
            <span style={S.multiDropdownPlaceholder}>{placeholder}</span>
          ) : (
            <div style={S.multiDropdownSelectedWrap}>
              {summaryOptions.map((option) => (
                <button
                  key={String(option.id)}
                  type="button"
                  style={S.multiDropdownSelectedChip}
                  disabled={disabled}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!disabled) onToggle(option.id);
                  }}
                >
                  <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                  {!disabled && <span style={S.multiDropdownSelectedChipRemove}>x</span>}
                </button>
              ))}
              {remainingSelectedCount > 0 && (
                <span style={{ ...S.multiDropdownSelectedChip, ...S.multiDropdownSelectedChipMuted }}>
                  +{remainingSelectedCount} more
                </span>
              )}
            </div>
          )}
        </div>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>

      <div style={S.multiDropdownMenu}>
        {!disabled && (
          <input
            type="text"
            style={S.multiDropdownSearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={onCreate ? "Search or create..." : "Search options..."}
          />
        )}

        {!disabled && selectedOptions.length > 0 && (
          <div style={S.multiDropdownSelectedPanel}>
            {selectedOptions.map((option) => (
              <button
                key={String(option.id)}
                type="button"
                style={S.multiDropdownSelectedChip}
                onClick={() => onToggle(option.id)}
              >
                <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                <span style={S.multiDropdownSelectedChipRemove}>x</span>
              </button>
            ))}
          </div>
        )}

        {disabled ? (
          <div style={S.multiDropdownEmpty}>{placeholder}</div>
        ) : (
          <div style={S.multiDropdownOptions}>
            {visibleOptions.length === 0 && !canCreate ? (
              <div style={S.multiDropdownEmpty}>No options available.</div>
            ) : (
              <>
                {visibleOptions.map((option) => {
                  const active = selectedSet.has(String(option.id));

                  return (
                    <label
                      key={String(option.id)}
                      style={{ ...S.checkboxOption, ...(active ? S.checkboxOptionActive : {}) }}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => onToggle(option.id)}
                        style={S.checkboxInput}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}

                {canCreate && (
                  <button
                    type="button"
                    style={{ ...S.checkboxOption, ...S.createOption }}
                    disabled={creating}
                    onClick={async () => {
                      setCreating(true);
                      try {
                        await onCreate(cleanQuery);
                        setQuery("");
                      } finally {
                        setCreating(false);
                      }
                    }}
                  >
                    <span style={S.createOptionIcon}>+</span>
                    <span>{creating ? "Creating..." : `${createPrefix} "${cleanQuery}"`}</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function getLocationCountryName(location) {
  return String(location?.country || location?.country_name || "India").trim();
}

function getLocationCountryId(location) {
  return location?.country_id || location?.countryId || location?.countryID || "";
}

function getCompactLocationOptionLabel(location) {
  if (!location) return "";
  return [
    location.city || location.name,
    location.state || location.state_name || location.stateName,
    getLocationCountryName(location),
  ]
    .filter(Boolean)
    .join(", ");
}

function getLocationOptionLabel(location) {
  if (!location) return "";
  return [
    location.city || location.name,
    location.tehsil || location.tehsil_name || location.tehsilName,
    location.district || location.district_name || location.districtName,
    location.state || location.state_name || location.stateName,
  ]
    .filter(Boolean)
    .join(", ");
}

function buildCityTierMap(cityTiers) {
  const map = new Map();
  safeArray(cityTiers).forEach((tier) => {
    safeNumberArray(tier?.city_ids).forEach((cityId) => {
      const existing = map.get(Number(cityId)) || [];
      existing.push(Number(tier.id));
      map.set(Number(cityId), existing);
    });
  });
  return map;
}

function isSameLocationCountry(location, country) {
  if (!country) return true;
  return getLocationCountryName(location).toLowerCase() === String(country).trim().toLowerCase();
}

function CityPreferenceDropdown({ placeholder, options, tierOptions, selected, onToggle }) {
  const [query, setQuery] = useState("");
  const [activeTierId, setActiveTierId] = useState("all");
  const cleanQuery = query.trim().toLowerCase();
  const selectedSet = new Set(safeArray(selected).map((value) => String(value)));
  const selectedOptions = safeArray(options).filter((option) => selectedSet.has(String(option.id)));
  const summaryOptions = selectedOptions.slice(0, 2);
  const remainingSelectedCount = Math.max(0, selectedOptions.length - summaryOptions.length);
  const visibleOptions = safeArray(options).filter((option) => {
    const matchesTier = activeTierId === "all" || safeNumberArray(option.tierIds).includes(Number(activeTierId));
    const matchesQuery = String(option.label || "").toLowerCase().includes(cleanQuery);
    return matchesTier && matchesQuery;
  });

  return (
    <details style={S.multiDropdown}>
      <summary style={S.multiDropdownSummary}>
        <div style={S.multiDropdownSummaryText}>
          {selectedOptions.length === 0 ? (
            <span style={S.multiDropdownPlaceholder}>{placeholder}</span>
          ) : (
            <div style={S.multiDropdownSelectedWrap}>
              {summaryOptions.map((option) => (
                <button
                  key={String(option.id)}
                  type="button"
                  style={S.multiDropdownSelectedChip}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggle(option.id);
                  }}
                >
                  <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                  <span style={S.multiDropdownSelectedChipRemove}>x</span>
                </button>
              ))}
              {remainingSelectedCount > 0 && (
                <span style={{ ...S.multiDropdownSelectedChip, ...S.multiDropdownSelectedChipMuted }}>
                  +{remainingSelectedCount} more
                </span>
              )}
            </div>
          )}
        </div>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>

      <div style={S.multiDropdownMenu}>
        <input
          type="text"
          style={S.multiDropdownSearch}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search city, state, country..."
        />

        {selectedOptions.length > 0 && (
          <div style={S.multiDropdownSelectedPanel}>
            {selectedOptions.map((option) => (
              <button
                key={String(option.id)}
                type="button"
                style={S.multiDropdownSelectedChip}
                onClick={() => onToggle(option.id)}
              >
                <span style={S.multiDropdownSelectedChipLabel}>{option.label}</span>
                <span style={S.multiDropdownSelectedChipRemove}>x</span>
              </button>
            ))}
          </div>
        )}

        <div style={S.cityTierFilterRow}>
          <button
            type="button"
            style={{ ...S.cityTierFilterChip, ...(activeTierId === "all" ? S.cityTierFilterChipActive : {}) }}
            onClick={() => setActiveTierId("all")}
          >
            All Tiers
          </button>
          {safeArray(tierOptions).map((tier) => (
            <button
              key={tier.id}
              type="button"
              style={{ ...S.cityTierFilterChip, ...(Number(activeTierId) === Number(tier.id) ? S.cityTierFilterChipActive : {}) }}
              onClick={() => setActiveTierId(String(tier.id))}
            >
              {tier.name || `Tier ${tier.id}`}
            </button>
          ))}
        </div>

        <div style={S.multiDropdownOptions}>
          {visibleOptions.length === 0 ? (
            <div style={S.multiDropdownEmpty}>No cities available.</div>
          ) : (
            visibleOptions.map((option) => {
              const active = selectedSet.has(String(option.id));
              return (
                <label
                  key={String(option.id)}
                  style={{ ...S.checkboxOption, ...(active ? S.checkboxOptionActive : {}) }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => onToggle(option.id)}
                    style={S.checkboxInput}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}

function LocationAutocompleteField({
  value,
  locations,
  onChange,
  inputStyle,
  placeholder = "Search location",
  getOptionLabel = getLocationOptionLabel,
}) {
  const wrapperRef = useRef(null);
  const safeLocations = useMemo(() => safeArray(locations), [locations]);
  const selectedLocation = useMemo(
    () => safeLocations.find((location) => Number(location.id) === Number(value)) || null,
    [safeLocations, value]
  );
  const selectedLabel = useMemo(() => getOptionLabel(selectedLocation), [getOptionLabel, selectedLocation]);
  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
        const normalizedQuery = String(query || "").trim().toLowerCase();
        if (!normalizedQuery) {
          onChange("");
          setQuery("");
          return;
        }
        const exactMatch = safeLocations.find(
          (location) => getOptionLabel(location).toLowerCase() === normalizedQuery
        );
        if (exactMatch) {
          onChange(String(exactMatch.id));
          setQuery(getOptionLabel(exactMatch));
          return;
        }
        if (selectedLocation) {
          setQuery(selectedLabel);
          return;
        }
        setQuery("");
        onChange("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [getOptionLabel, open, onChange, query, safeLocations, selectedLabel, selectedLocation]);

  const filteredLocations = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    const sortedLocations = safeLocations
      .slice()
      .sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b)));

    if (!normalizedQuery) return sortedLocations.slice(0, 50);
    return sortedLocations
      .filter((location) => getOptionLabel(location).toLowerCase().includes(normalizedQuery))
      .slice(0, 50);
  }, [getOptionLabel, query, safeLocations]);

  const handleSelect = (location) => {
    onChange(String(location.id));
    setQuery(getOptionLabel(location));
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={S.autocompleteWrap}>
      <input
        style={inputStyle}
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div style={S.autocompleteMenu}>
          {filteredLocations.length ? (
            filteredLocations.map((location) => (
              <button
                key={location.id}
                type="button"
                style={S.autocompleteOption}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(location);
                }}
              >
                {getOptionLabel(location)}
              </button>
            ))
          ) : (
            <div style={S.autocompleteEmpty}>No cities found.</div>
          )}
        </div>
      )}
    </div>
  );
}

function getLocationCityName(location) {
  return String(location?.city || location?.name || "").trim();
}

function LocationPicker({ lookups, values, onChange }) {
  const locations = safeArray(lookups.locations);

  const countries = useMemo(() => {
    const set = new Set(locations.map((l) => getLocationCountryName(l)).filter(Boolean));
    if (!set.size) set.add("India");
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ id: country, label: country }));
  }, [locations]);

  const states = useMemo(() => {
    return uniqueBy(
      locations
        .filter((l) => isSameLocationCountry(l, values.country))
        .map((l) => ({
          id: l.state_id || l.stateId || l.stateID,
          label: l.state || l.state_name || l.stateName,
        }))
        .filter((x) => x.id && x.label),
      "id"
    );
  }, [locations, values.country]);

  const districts = useMemo(() => {
    return uniqueBy(
      locations
        .filter((l) => isSameLocationCountry(l, values.country))
        .filter((l) => !values.state_id || Number(l.state_id || l.stateId || l.stateID) === Number(values.state_id))
        .map((l) => ({
          id: l.district_id || l.districtId || l.districtID,
          label: l.district || l.district_name || l.districtName,
        }))
        .filter((x) => x.id && x.label),
      "id"
    );
  }, [locations, values.country, values.state_id]);

  const tehsils = useMemo(() => {
    return uniqueBy(
      locations
        .filter((l) => isSameLocationCountry(l, values.country))
        .filter((l) => !values.state_id || Number(l.state_id || l.stateId || l.stateID) === Number(values.state_id))
        .filter((l) => !values.district_id || Number(l.district_id || l.districtId || l.districtID) === Number(values.district_id))
        .map((l) => ({
          id: l.tehsil_id || l.tehsilId || l.tehsilID,
          label: l.tehsil || l.tehsil_name || l.tehsilName,
        }))
        .filter((x) => x.id && x.label),
      "id"
    );
  }, [locations, values.country, values.state_id, values.district_id]);

  const cities = useMemo(() => {
    return locations
      .filter((l) => isSameLocationCountry(l, values.country))
      .filter((l) => !values.state_id || Number(l.state_id || l.stateId || l.stateID) === Number(values.state_id))
      .filter((l) => !values.district_id || Number(l.district_id || l.districtId || l.districtID) === Number(values.district_id))
      .filter((l) => !values.tehsil_id || Number(l.tehsil_id || l.tehsilId || l.tehsilID) === Number(values.tehsil_id))
      .map((l) => ({
        id: l.id,
        label: [l.city || l.name, l.tehsil || l.tehsil_name, l.district || l.district_name, l.state || l.state_name].filter(Boolean).join(", "),
      }))
      .slice()
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [locations, values.country, values.state_id, values.district_id, values.tehsil_id]);

  return (
    <div style={S.locationGrid}>
      <SearchableSelect
        value={values.country || ""}
        options={countries}
        onChange={onChange.country}
        placeholder="Country"
      />

      <SearchableSelect
        value={values.state_id || ""}
        options={states}
        onChange={onChange.state_id}
        placeholder="State"
      />

      <SearchableSelect
        value={values.district_id || ""}
        options={districts}
        onChange={onChange.district_id}
        placeholder="District"
        disabled={!values.state_id}
      />

      <SearchableSelect
        value={values.tehsil_id || ""}
        options={tehsils}
        onChange={onChange.tehsil_id}
        placeholder="Tehsil"
        disabled={!values.district_id}
      />

      <SearchableSelect
        value={values.city_id || ""}
        options={cities}
        onChange={onChange.city_id}
        placeholder="City"
        disabled={!values.tehsil_id && tehsils.length > 0}
        containerStyle={{ gridColumn: "1 / -1" }}
      />
    </div>
  );
}

function AddressLocationFields({ lookups, values, onChange }) {
  const locations = safeArray(lookups.locations);
  const showVisaFields = shouldShowAddressVisaFields(values, locations);

  const countries = useMemo(() => {
    return uniqueBy(
      locations
        .map((location) => ({
          id: location.country_id || location.countryId || location.countryID,
          name: location.country || location.country_name || location.countryName || "India",
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations]);

  const states = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .map((location) => ({
          id: location.state_id || location.stateId || location.stateID,
          name: location.state || location.state_name || location.stateName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id]);

  const districts = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .map((location) => ({
          id: location.district_id || location.districtId || location.districtID,
          name: location.district || location.district_name || location.districtName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id, values.state_id]);

  const tehsils = useMemo(() => {
    return uniqueBy(
      locations
        .filter((location) => !values.country_id || Number(location.country_id || location.countryId || location.countryID) === Number(values.country_id))
        .filter((location) => !values.state_id || Number(location.state_id || location.stateId || location.stateID) === Number(values.state_id))
        .filter((location) => !values.district_id || Number(location.district_id || location.districtId || location.districtID) === Number(values.district_id))
        .map((location) => ({
          id: location.tehsil_id || location.tehsilId || location.tehsilID,
          name: location.tehsil || location.tehsil_name || location.tehsilName,
        }))
        .filter((option) => option.id && option.name),
      "id"
    ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [locations, values.country_id, values.state_id, values.district_id]);

  return (
    <>
      <Field label="Country">
        <SearchableSelect
          value={values.country_id || ""}
          options={countries.map((country) => ({ id: country.id, label: country.name }))}
          onChange={onChange.country_id}
          placeholder="Select country"
          fieldShellStyle={S.input}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="State">
        <SearchableSelect
          value={values.state_id || ""}
          options={states.map((state) => ({ id: state.id, label: state.name }))}
          onChange={onChange.state_id}
          placeholder="Select state"
          disabled={!values.country_id}
          fieldShellStyle={S.input}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="District">
        <SearchableSelect
          value={values.district_id || ""}
          options={districts.map((district) => ({ id: district.id, label: district.name }))}
          onChange={onChange.district_id}
          placeholder="Select district"
          disabled={!values.state_id}
          fieldShellStyle={S.input}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      <Field label="Tehsil">
        <SearchableSelect
          value={values.tehsil_id || ""}
          options={tehsils.map((tehsil) => ({ id: tehsil.id, label: tehsil.name }))}
          onChange={onChange.tehsil_id}
          placeholder="Select tehsil"
          disabled={!values.district_id}
          fieldShellStyle={S.input}
          fieldShellDisabledStyle={{ background: C.soft, color: C.muted }}
          inputStyle={S.searchableSelectInput}
          chevronButtonStyle={S.searchableSelectChevronButton}
          menuStyle={S.searchableSelectMenu}
          optionStyle={S.searchableSelectOption}
          optionActiveStyle={S.searchableSelectOptionActive}
        />
      </Field>

      {showVisaFields && (
        <>
          <Field label="Visa Status">
            <select style={S.input} value={values.visa_status_id || ""} onChange={(e) => onChange.visa_status_id(e.target.value)}>
              <option value="">Select visa status</option>
              {safeArray(lookups.visaStatuses).map((visaStatus) => (
                <option key={visaStatus.id} value={visaStatus.id}>{visaStatus.name || visaStatus.display_label}</option>
              ))}
            </select>
          </Field>

          <Field label="Visa Name">
            <select style={S.input} value={values.visa_name_id || ""} onChange={(e) => onChange.visa_name_id(e.target.value)}>
              <option value="">Select visa name</option>
              {safeArray(lookups.visaNames).map((visaName) => (
                <option key={visaName.id} value={visaName.id}>{visaName.name || visaName.display_label}</option>
              ))}
            </select>
          </Field>
        </>
      )}
    </>
  );
}

function Field({ label, children, error, span2 }) {
  return (
    <div style={{ gridColumn: span2 ? "1 / -1" : undefined }}>
      <label style={S.label}>{label}</label>
      {children}
      {error && <span style={S.errMsg}>{error}</span>}
    </div>
  );
}

function SectionHead({ title, onAdd, small }) {
  return (
    <div style={S.sectionHead}>
      <p style={{ ...S.sectionTitle, marginBottom: 0, ...(small ? { fontSize: 11 } : {}) }}>{title}</p>
      {onAdd && <button type="button" style={S.addBtn} onClick={onAdd}>+ Add</button>}
    </div>
  );
}

function RemoveBtn({ onClick }) {
  return (
    <button type="button" onClick={onClick} style={S.removeBtn} title="Remove">
      x
    </button>
  );
}

function formatDateTimeLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getCreateProfileDraftKey(user, scope = "") {
  const userKey = String(user?.id || user?.email || "default").trim() || "default";
  const scopeKey = String(scope || "").trim();
  return scopeKey
    ? `jk:create-profile-draft:${userKey}:${scopeKey}`
    : `jk:create-profile-draft:${userKey}`;
}

function readCreateProfileDraft(key) {
  if (typeof window === "undefined" || !key) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (err) {
    console.warn("Unable to read create profile draft", err);
    return null;
  }
}

function clearCreateProfileDraft(key) {
  if (typeof window === "undefined" || !key) return;

  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    console.warn("Unable to clear create profile draft", err);
  }
}

function normalizeCreateProfileStep(value) {
  const stepNumber = Number(value);
  if (!Number.isFinite(stepNumber)) return 0;
  return Math.max(0, Math.min(STEPS.length - 1, Math.floor(stepNumber)));
}

function mergeCreateProfileDraftForm(draftForm) {
  if (!draftForm || typeof draftForm !== "object") return defaultForm;

  return {
    ...defaultForm,
    ...draftForm,
    preferences: {
      ...defaultForm.preferences,
      ...(draftForm.preferences || {}),
    },
    educations: safeArray(draftForm.educations).length ? safeArray(draftForm.educations) : defaultForm.educations,
    occupations: safeArray(draftForm.occupations).length ? safeArray(draftForm.occupations) : defaultForm.occupations,
    relatives: safeArray(draftForm.relatives).length ? safeArray(draftForm.relatives) : defaultForm.relatives,
    addresses: safeArray(draftForm.addresses).length ? safeArray(draftForm.addresses) : defaultForm.addresses,
    contacts: safeArray(draftForm.contacts),
    tags: safeArray(draftForm.tags),
  };
}

function convertPreferredAgeToMonths(value, unit = "months") {
  if (value === "" || value === null || value === undefined) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return unit === "years" ? numericValue * 12 : numericValue;
}


function buildDuplicateReviewPayload({ duplicateMatches, duplicateAction, duplicateTargetId, duplicateResolutionMode }) {
  if (!duplicateMatches?.length) return null;

  return {
    action: duplicateAction || "retain",
    selected_duplicate_profile_id: duplicateTargetId || null,
    resolution_mode: duplicateResolutionMode === "later" ? "now" : (duplicateResolutionMode || "now"),
    matches: duplicateMatches.map((m) => ({
      duplicate_profile_id: m.candidate?.id,
      profile_no: m.candidate?.profile_no || null,
      score: Number(m.score || 0),
      reason: Array.isArray(m.reasons) ? m.reasons.join("; ") : String(m.reason || ""),
      selected: duplicateTargetId
        ? String(m.candidate?.id) === String(duplicateTargetId)
        : false,
    })),
  };
}

function pickPayloadFields(payload, keys = []) {
  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}

function buildInitialProfilePayload(form, lookups) {
  const payload = buildPayload(form, lookups);

  return pickPayloadFields(payload, [
    "first_name",
    "middle_name",
    "last_name",
    "gender_id",
    "gender",
    "birth_date",
    "dob",
    "birth_time",
    "birth_country",
    "birth_state_id",
    "birth_district_id",
    "birth_tehsil_id",
    "birth_place_city_id",
    "birth_place_id",
    "birth_latitude",
    "birth_longitude",
    "caste_id",
    "sakha_id",
    "dadisa_sakha_id",
    "nanisa_sakha_id",
    "mamasa_sakha_id",
    "sampraday_id",
    "sampraday_details",
    "about_me",
    "referral_source_id",
    "source_type",
    "status",
  ]);
}

function validateFutureProspectRelatives(relatives) {
  const items = Array.isArray(relatives) ? relatives : [];
  const invalidIndex = items.findIndex((relative) => {
    if (!relative?.is_future_prospect || !isRelativeUnmarriedStatus(relative?.marital_status)) return false;
    const rawYear = String(relative?.future_prospect_eligible_year || "").trim();
    if (!rawYear) return false;
    return !/^\d{4}$/.test(rawYear);
  });
  return invalidIndex >= 0 ? `Eligible year must be a 4-digit year for relative #${invalidIndex + 1}.` : "";
}

function validatePhoneContacts(contacts, label = "Contact") {
  const items = Array.isArray(contacts) ? contacts : [];
  for (let index = 0; index < items.length; index += 1) {
    const contact = items[index];
    if (!isIndianPhoneContactType(contact?.contact_type)) continue;
    const contactValue = String(contact?.contact_value || "").trim();
    if (!contactValue) continue;
    const error = validateIndianPhoneValue(contactValue);
    if (error) {
      return `${label} #${index + 1}: ${error}`;
    }
  }
  return "";
}

function validateRelativePhoneContacts(relatives) {
  const items = Array.isArray(relatives) ? relatives : [];
  for (let index = 0; index < items.length; index += 1) {
    const error = validatePhoneContacts(items[index]?.contacts, `Relative #${index + 1} contact`);
    if (error) return error;
  }
  return "";
}

function isRelativeUnmarriedStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "unmarried" || normalized === "single";
}

function normalizeRelativeRelationLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

const RELATION_GENDER_MAP = {
  father: "Male",
  mother: "Female",
  brother: "Male",
  sister: "Female",
  chachaji: "Male",
  bua: "Female",
  mamaji: "Male",
  masi: "Female",
  dada: "Male",
  dadi: "Female",
  nana: "Male",
  nani: "Female",
};

function isFamilyRelationType(value) {
  return FAMILY_RELATION_TYPES.has(normalizeRelativeRelationLabel(value));
}

function normalizeRelativeFamilySide(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "maternal" ? "maternal" : normalized === "paternal" ? "paternal" : "paternal";
}

function isFamilySectionRelative(relative, relationTypes = []) {
  const relationLabel = getRelativeRelationLabel(relative, relationTypes);
  if (isFamilyRelationType(relationLabel)) return true;
  return String(relative?.family_side || "").trim().toLowerCase() === "none";
}

function normalizeRelativeForSection(relative, relationTypes = []) {
  if (!relative || typeof relative !== "object") return { ...emptyRelative };
  const relationLabel = getRelativeRelationLabel(relative, relationTypes);
  if (isFamilyRelationType(relationLabel) || String(relative.family_side || "").trim().toLowerCase() === "none") {
    return { ...relative, family_side: "none" };
  }
  return { ...relative, family_side: normalizeRelativeFamilySide(relative.family_side) };
}

function applyRelativeGenderMapping(relative, relationTypes = []) {
  const relationLabel = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  const mappedGender = RELATION_GENDER_MAP[relationLabel];
  if (!mappedGender) return relative;
  return {
    ...relative,
    gender: mappedGender,
  };
}

function getRelativeNameParts(relative) {
  const explicitFirst = String(relative?.first_name || "").trim();
  const explicitMiddle = String(relative?.middle_name || "").trim();
  const explicitLast = String(relative?.last_name || "").trim();
  const fullNameParts = String(relative?.full_name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    first_name: explicitFirst || fullNameParts[0] || "",
    middle_name: explicitMiddle || "",
    last_name: explicitLast || (fullNameParts.length > 1 ? fullNameParts[fullNameParts.length - 1] : ""),
  };
}

function getFatherNameDefaults(relatives, currentIndex, relationTypes = []) {
  const father = safeArray(relatives).find((relative, index) => index !== currentIndex && normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes)) === "father");
  if (!father) return null;
  const fatherName = getRelativeNameParts(father);
  return {
    middle_name: fatherName.first_name,
    last_name: fatherName.last_name,
  };
}

function applySiblingNameDefaults(relative, relatives, currentIndex, relationTypes = []) {
  const relationLabel = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  if (relationLabel !== "brother" && relationLabel !== "sister") return relative;
  if (String(relative?.middle_name || "").trim() && String(relative?.last_name || "").trim()) return relative;

  const fatherDefaults = getFatherNameDefaults(relatives, currentIndex, relationTypes);
  if (!fatherDefaults) return relative;

  return {
    ...relative,
    middle_name: String(relative?.middle_name || "").trim() || fatherDefaults.middle_name || "",
    last_name: String(relative?.last_name || "").trim() || fatherDefaults.last_name || "",
  };
}

function shouldShowRelativeMaritalStatus(relative, relationTypes = []) {
  const normalizedRelationType = normalizeRelativeRelationLabel(getRelativeRelationLabel(relative, relationTypes));
  return normalizedRelationType === "sister" || normalizedRelationType === "brother" || normalizedRelationType === "sibling";
}

function clearRelativeFutureProspectFields(relative) {
  return {
    ...relative,
    is_future_prospect: false,
    future_prospect_birth_date: "",
    future_prospect_eligible_year: "",
  };
}

function clearRelativeMaritalFields(relative) {
  return {
    ...clearRelativeFutureProspectFields(relative),
    marital_status: "",
  };
}

function getSelfContactName(form) {
  return [form?.first_name, form?.middle_name, form?.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
}

function getRelativeDisplayName(relative) {
  return (
    relative?.full_name ||
    [relative?.first_name, relative?.middle_name, relative?.last_name]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ")
  );
}

function getRelativeRelationLabel(relative, relationTypes = []) {
  return getLookupName(relationTypes, relative?.relation_type_id) || relative?.relation_type || "";
}

function splitRelativeContactValues(value) {
  return String(value || "")
    .split(/[,\n;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildRelativeContacts(relative) {
  const existingContacts = safeArray(relative?.contacts)
    .filter((contact) => String(contact?.contact_value || "").trim())
    .map((contact) => ({
      ...contact,
      contact_value: isIndianPhoneContactType(contact?.contact_type)
        ? getIndianPhoneInputValue(contact?.contact_value || contact?.normalized_value || "")
        : String(contact?.contact_value || "").trim(),
    }));
  if (existingContacts.length) return existingContacts;

  return [
    ...splitRelativeContactValues(relative?.phone).map((value) => ({ contact_type: "mobile", contact_value: getIndianPhoneInputValue(value) })),
    ...splitRelativeContactValues(relative?.email).map((value) => ({ contact_type: "email", contact_value: value })),
  ];
}

function deriveRelativeContactValues(relative) {
  const contacts = buildRelativeContacts(relative);
  const phoneValues = contacts
    .filter((contact) => isIndianPhoneContactType(contact?.contact_type))
    .map((contact) => toStoredIndianPhoneValue(contact?.contact_value || ""))
    .filter(Boolean);
  const emailValues = contacts
    .filter((contact) => String(contact?.contact_type || "").toLowerCase() === "email")
    .map((contact) => String(contact?.contact_value || "").trim())
    .filter(Boolean);

  return {
    phone: phoneValues.join(", "),
    email: emailValues.join(", "),
  };
}

function isProfilePhoneContactType(type) {
  return isIndianPhoneContactType(type);
}

function normalizeSelfContactType(contactType) {
  const normalized = String(contactType || "").trim().toLowerCase();
  return ["mobile", "whatsapp", "email"].includes(normalized) ? normalized : "mobile";
}

function normalizeSelfContact(contact, form) {
  const contactType = normalizeSelfContactType(contact?.contact_type);
  const contactValue = String(contact?.contact_value || "").trim();
  const storedContactValue = isProfilePhoneContactType(contactType)
    ? toStoredIndianPhoneValue(contactValue)
    : contactValue;

  return {
    ...contact,
    contact_type: contactType,
    contact_value: storedContactValue,
    normalized_value: isProfilePhoneContactType(contactType)
      ? normalizeIndianPhone(contactValue)
      : String(contactValue).toLowerCase(),
    contact_person_name: getSelfContactName(form),
    contact_person_relation: "self",
  };
}

function deriveProfileContactValues(contacts) {
  const mobile =
    contacts.find((contact) => contact.contact_type === "mobile")?.contact_value ||
    contacts.find((contact) => contact.contact_type === "whatsapp")?.contact_value ||
    contacts.find((contact) => contact.contact_type === "parent_mobile")?.contact_value ||
    "";
  const email = contacts.find((contact) => contact.contact_type === "email")?.contact_value || "";

  return {
    mobile: mobile || null,
    email: email || null,
  };
}

function buildPayload(form, lookups) {
  const gender = getLookupName(lookups.genders, form.gender_id) || form.gender;
  const marital_status = getLookupName(lookups.maritalStatus, form.marital_status_id) || form.marital_status || "single";
  const complexion = getLookupName(lookups.complexions, form.complexion_id) || form.complexion;
  const blood_group = getLookupName(lookups.bloodGroups, form.blood_group_id) || form.blood_group;

  const highestEdu =
    form.educations.find((e) => e.is_highest_degree) ||
    form.educations.find((e) => e.degree_name || e.institution_name) ||
    form.educations[0];

  const occupations = safeArray(form.occupations)
    .filter(occupationHasContent)
    .map((occupation, index) => normalizeOccupationPayloadRow(occupation, lookups.occupationTypes, index));
  const occupation = occupations[0] || {};
  const occupation_type =
    getLookupName(lookups.occupationTypes, occupation.occupation_type_id) ||
    occupation.occupation_type ||
    null;

  const contacts = safeArray(form.contacts)
    .filter((contact) => String(contact.contact_value || "").trim())
    .map((contact) => normalizeSelfContact(contact, form));
  const profileContactValues = deriveProfileContactValues(contacts);

  return {
    first_name: form.first_name,
    middle_name: form.middle_name || null,
    last_name: form.last_name,
    gender_id: form.gender_id || null,
    gender,
    birth_date: form.birth_date || form.dob,
    dob: form.birth_date || form.dob,
    birth_time: form.birth_time || null,

    birth_country: form.birth_country || "India",
    birth_state_id: form.birth_state_id || null,
    birth_district_id: form.birth_district_id || null,
    birth_tehsil_id: form.birth_tehsil_id || null,
    birth_place: form.birth_place || null,
    birth_place_city_id: form.birth_place_city_id || form.birth_place_id || null,
    birth_place_id: form.birth_place_city_id || form.birth_place_id || null,
    birth_latitude: form.birth_latitude || null,
    birth_longitude: form.birth_longitude || null,

    country: form.country || "India",
    state_id: form.state_id || null,
    district_id: form.district_id || null,
    tehsil_id: form.tehsil_id || null,
    city_id: form.city_id || form.current_location_id || null,
    current_location_id: form.city_id || form.current_location_id || null,

    marital_status_id: form.marital_status_id || null,
    marital_status,
    marital_status_description: form.marital_status_description || null,
    has_children: Boolean(form.has_children),
    marital_field_details: form.marital_field_details || null,
    height_cm: form.height_cm || form.height || null,
    height: form.height_cm || form.height || null,
    complexion_id: form.complexion_id || null,
    complexion,
    blood_group_id: form.blood_group_id || null,
    blood_group,
    manglik: form.manglik || "Do Not Know",
    sampraday_id: form.sampraday_id || null,
    sampraday_details: form.sampraday_details || null,
    has_disability: Boolean(form.has_disability),
    disability_description: form.disability_description || null,
    about_me: form.about_me || null,

    caste_id: form.caste_id || null,
    sakha_id: form.sakha_id || null,
    dadisa_sakha_id: form.dadisa_sakha_id || null,
    nanisa_sakha_id: form.nanisa_sakha_id || null,
    mamasa_sakha_id: form.mamasa_sakha_id || null,
    village_id: form.village_id || null,

    education_category_id: highestEdu?.education_category_id || null,
    education_summary: [highestEdu?.degree_name, highestEdu?.education_description].filter(Boolean).join(" - ") || null,

    occupation_type_id: occupation.occupation_type_id || null,
    occupation_type,
    company_name: occupation.company_name || null,
    current_occupation_details: occupation.occupation_details || null,
    work_profile: occupation.work_profile || null,
    annual_income_amount: occupation.annual_income_amount || null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      occupation.annual_income_currency_code,
      occupation.annual_income_amount
    ),
    annual_turnover_amount: occupation.annual_turnover_amount || null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      occupation.annual_turnover_currency_code,
      occupation.annual_turnover_amount
    ),

    contacts,
    mobile: profileContactValues.mobile,
    email: profileContactValues.email,

    educations: form.educations
      .filter((e) => e.degree_name || e.institution_name || e.education_description)
      .map((e, index) => {
        const educationLocation = safeArray(lookups.locations).find((location) => Number(location.id) === Number(e.education_city_id));
        return {
          ...e,
          education_country_id: e.education_country_id || getLocationCountryId(educationLocation),
          education_location: e.education_location || getLocationOptionLabel(educationLocation),
          sort_order: e.sort_order || index + 1,
          is_highest_degree: Boolean(e.is_highest_degree),
        };
      }),

    occupations,

    relatives: form.relatives
      .map((r, index) => {
        const normalizedRelative = normalizeRelativeForSection(r, lookups.relationTypes);
        const relativeContactValues = deriveRelativeContactValues(r);
        const showRelativeMaritalStatus = shouldShowRelativeMaritalStatus(normalizedRelative, lookups.relationTypes);
        const isUnmarriedRelative = showRelativeMaritalStatus && isRelativeUnmarriedStatus(normalizedRelative.marital_status);
        const relativeBase = { ...normalizedRelative };
        delete relativeBase.is_relative;
        return {
          ...relativeBase,
          phone: relativeContactValues.phone || null,
          email: relativeContactValues.email || null,
          relation_type:
            getLookupName(lookups.relationTypes, normalizedRelative.relation_type_id) ||
            normalizedRelative.relation_type ||
            null,
          occupation_type:
            getLookupName(lookups.occupationTypes, normalizedRelative.occupation_type_id) ||
            normalizedRelative.occupation_type ||
            null,
          marital_status: showRelativeMaritalStatus ? (normalizedRelative.marital_status || null) : null,
          is_future_prospect: isUnmarriedRelative && Boolean(normalizedRelative.is_future_prospect),
          future_prospect_birth_date: isUnmarriedRelative && normalizedRelative.is_future_prospect ? (normalizedRelative.future_prospect_birth_date || null) : null,
          future_prospect_eligible_year: isUnmarriedRelative && normalizedRelative.is_future_prospect ? (normalizedRelative.future_prospect_eligible_year || null) : null,
          full_name: normalizedRelative.full_name || [normalizedRelative.first_name, normalizedRelative.middle_name, normalizedRelative.last_name].filter(Boolean).join(" "),
          sort_order: normalizedRelative.sort_order || index + 1,
          education: (normalizedRelative.education || []).filter((e) => e.degree_name || e.institution_name || e.education_description),
        };
      })
      .filter((r) => r.full_name || r.first_name || r.relation_type),

    addresses: form.addresses
      .filter((a) => a.country_id || a.address_line1 || a.address_line2 || a.city_id || a.pincode || a.visa_status_id || a.visa_name_id)
      .map((a) => ({
        ...a,
        country_id: a.country_id || null,
        state_id: a.state_id || null,
        district_id: a.district_id || null,
        tehsil_id: a.tehsil_id || null,
        city_id: a.city_id || null,
        village_id: a.village_id || null,
        visa_status_id: a.visa_status_id || null,
        visa_name_id: a.visa_name_id || null,
        pincode: a.pincode || null,
        is_primary: false,
      })),

    preferences: {
      ...form.preferences,
      preferred_age_gap_months: null,
      preferred_city_tier_ids: [],
      preferred_city_ids: safeNumberArray(form.preferences.preferred_city_ids),
      non_preferred_city_ids: safeNumberArray(form.preferences.non_preferred_city_ids),
      preferred_education_category_ids: safeNumberArray(form.preferences.preferred_education_category_ids),
      non_preferred_education_category_ids: safeNumberArray(form.preferences.non_preferred_education_category_ids),
      preferred_degrees: safeArray(form.preferences.preferred_degrees).map((value) => String(value || "").trim()).filter(Boolean),
      non_preferred_degrees: safeArray(form.preferences.non_preferred_degrees).map((value) => String(value || "").trim()).filter(Boolean),
      preferred_occupation_types: safeArray(form.preferences.preferred_occupation_types).map((value) => String(value || "").trim()).filter(Boolean),
      non_preferred_occupation_types: safeArray(form.preferences.non_preferred_occupation_types).map((value) => String(value || "").trim()).filter(Boolean),
      preferred_age_min_unit:
        String(form.preferences.preferred_age_min_unit || "").trim().toLowerCase() === "years" ? "years" : "months",
      preferred_age_max_unit:
        String(form.preferences.preferred_age_max_unit || "").trim().toLowerCase() === "years" ? "years" : "months",
      preferred_age_min: convertPreferredAgeToMonths(form.preferences.preferred_age_min, form.preferences.preferred_age_min_unit),
      preferred_age_max: convertPreferredAgeToMonths(form.preferences.preferred_age_max, form.preferences.preferred_age_max_unit),
    },
    tags: form.tags,
    referral_source_id: form.referral_source_id || null,
    source_type: "team",
    status: "active",
  };
}

function scoreDuplicate(source, candidate) {
  let matchedWeight = 0;
  const reasons = [];
  const strictMatch = hasStrictDuplicateEvidence(source, candidate);

  const sDob = source.birth_date || source.dob;
  const cDob = candidate.birth_date || candidate.dob;
  const totalSignals = 5 + (source.birth_time ? 1 : 0);

  if (sDob && cDob && String(sDob).slice(0, 10) === String(cDob).slice(0, 10)) {
    matchedWeight += 1;
    reasons.push("Same date of birth");
  }

  if (source.birth_time && candidate.birth_time && String(source.birth_time).slice(0, 5) === String(candidate.birth_time).slice(0, 5)) {
    matchedWeight += 1;
    reasons.push("Same birth time");
  }

  if (source.gender && candidate.gender && normalizeText(source.gender) === normalizeText(candidate.gender)) {
    matchedWeight += 1;
    reasons.push("Same gender");
  }

  matchedWeight += scoreNameSignal(source.first_name, candidate.first_name, "first name", reasons);
  matchedWeight += scoreNameSignal(source.middle_name, candidate.middle_name, "middle name", reasons);
  matchedWeight += scoreNameSignal(source.last_name, candidate.last_name, "last name", reasons);

  return {
    score: Math.min(Number((matchedWeight / Math.max(totalSignals, 1)).toFixed(3)), 1),
    reasons,
    candidate,
    strictMatch,
  };
}

function hasStrictDuplicateEvidence(source, candidate) {
  const sourceFirst = normalizeIndianNamePart(source.first_name);
  const sourceMiddle = normalizeIndianNamePart(source.middle_name);
  const sourceLast = normalizeIndianNamePart(source.last_name);
  const candidateFirst = normalizeIndianNamePart(candidate.first_name);
  const candidateMiddle = normalizeIndianNamePart(candidate.middle_name);
  const candidateLast = normalizeIndianNamePart(candidate.last_name);
  const dobMatch =
    !!(source.birth_date || source.dob) &&
    !!(candidate.birth_date || candidate.dob) &&
    String(source.birth_date || source.dob).slice(0, 10) === String(candidate.birth_date || candidate.dob).slice(0, 10);
  const genderMatch =
    !!source.gender &&
    !!candidate.gender &&
    normalizeText(source.gender) === normalizeText(candidate.gender);
  const firstMatch = !!sourceFirst && !!candidateFirst && sourceFirst === candidateFirst;
  const middleMatch = !!sourceMiddle && !!candidateMiddle && sourceMiddle === candidateMiddle;
  const lastMatch = !!sourceLast && !!candidateLast && sourceLast === candidateLast;
  const sourceBirthTime = normalizeDuplicateBirthTime(source.birth_time);
  const candidateBirthTime = normalizeDuplicateBirthTime(candidate.birth_time);
  const birthTimeMatch = !sourceBirthTime || (candidateBirthTime && sourceBirthTime === candidateBirthTime);

  return firstMatch && middleMatch && lastMatch && genderMatch && dobMatch && birthTimeMatch;
}

function normalizeDuplicateBirthTime(value) {
  const normalized = String(value || "").trim();
  return normalized ? normalized.slice(0, 5) : "";
}

function scoreNameSignal(sourceValue, candidateValue, label, reasons) {
  const sourceName = normalizeIndianNamePart(sourceValue);
  const candidateName = normalizeIndianNamePart(candidateValue);
  if (!sourceName || !candidateName) return 0;

  if (sourceName === candidateName) {
    reasons.push(`Same ${label}`);
    return 1;
  }

  const sourcePhonetic = indianPhoneticCode(sourceName, "primary");
  const candidatePhonetic = indianPhoneticCode(candidateName, "primary");
  if (sourcePhonetic && candidatePhonetic && sourcePhonetic === candidatePhonetic) {
    reasons.push(`Similar ${label}`);
    return 0.8;
  }

  if (
    sourceName.length >= 3 &&
    candidateName.length >= 3 &&
    (sourceName.includes(candidateName) || candidateName.includes(sourceName))
  ) {
    reasons.push(`Close ${label}`);
    return 0.35;
  }

  const overlap = tokenOverlapRatio(sourceName, candidateName);
  if (overlap > 0.2) {
    reasons.push(`Related ${label}`);
    return Number((overlap * 0.45).toFixed(3));
  }

  return 0;
}

function tokenOverlapRatio(left, right) {
  const leftTokens = normalizeIndianNamePart(left).split(/\s+/).filter(Boolean);
  const rightTokens = normalizeIndianNamePart(right).split(/\s+/).filter(Boolean);
  if (!leftTokens.length || !rightTokens.length) return 0;

  const rightTokenSet = new Set(rightTokens);
  const matches = leftTokens.filter((token) => rightTokenSet.has(token)).length;
  return matches / Math.max(leftTokens.length, rightTokens.length);
}

function simpleProfilePhoneticKey(firstName, middleName, lastName, fullName = "") {
  const parts = resolveIndianNameParts(firstName, middleName, lastName, fullName);
  if (!parts.length) return "";

  const primary = parts.map((part) => indianPhoneticCode(part, "primary")).filter(Boolean).join("-");
  return primary || "";
}

function resolveIndianNameParts(firstName, middleName, lastName, fullName = "") {
  let first = firstName || "";
  let middle = middleName || "";
  let last = lastName || "";

  if ((!first || !last) && fullName) {
    const tokens = normalizeText(fullName).split(/\s+/).filter(Boolean);
    first = first || tokens[0] || "";

    if (!middle && tokens.length > 2) {
      middle = tokens.slice(1, -1).join(" ");
    }

    last = last || tokens[tokens.length - 1] || "";
  }

  return [first, middle, last]
    .map(normalizeIndianNamePart)
    .filter(Boolean);
}

function normalizeIndianNamePart(value) {
  return normalizeText(value)
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

function indianPhoneticCode(part, mode = "primary") {
  return normalizeIndianNamePart(part)
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => indianPhoneticToken(token, mode))
    .filter(Boolean)
    .join("");
}

function indianPhoneticToken(token, mode = "primary") {
  let value = normalizeText(token).replace(/[^a-z0-9]/g, "");
  if (!value) return "";

  value = value
    .replace(/^kn/, "n")
    .replace(/^gn/, "n")
    .replace(/^pn/, "n")
    .replace(/^wr/, "r")
    .replace(/^x/, "s")
    .replace(/aa+/g, "a")
    .replace(/ee+|ii+/g, "i")
    .replace(/oo+|uu+/g, "u")
    .replace(/ou/g, "u")
    .replace(/ae/g, "e")
    .replace(/ck/g, "k")
    .replace(/q/g, "k")
    .replace(/tch/g, "ch")
    .replace(/chh/g, "ch")
    .replace(/sch/g, "s")
    .replace(/sh/g, "s")
    .replace(/zh/g, mode === "secondary" ? "j" : "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/kh/g, "k")
    .replace(/gh/g, "g")
    .replace(/jh/g, "j")
    .replace(/wh/g, mode === "secondary" ? "w" : "v")
    .replace(/w/g, mode === "secondary" ? "w" : "v")
    .replace(/v/g, mode === "secondary" ? "w" : "v")
    .replace(/z/g, mode === "secondary" ? "s" : "j")
    .replace(/c(?=[eiy])/g, mode === "secondary" ? "s" : "k")
    .replace(/c/g, "k")
    .replace(/y/g, "i")
    .replace(/h$/g, "")
    .replace(/(.)\1+/g, "$1");

  if (!value) return "";

  const mapped = mapIndianPhoneticChars(value, mode);
  if (!mapped) return "";

  const first = mapped[0];
  const tail = mapped
    .slice(1)
    .replace(/[AEIOU]/g, "")
    .replace(/(.)\1+/g, "$1");

  return `${first}${tail}`.slice(0, 8);
}

function mapIndianPhoneticChars(value, mode = "primary") {
  const map = {
    a: "A",
    b: "B",
    c: "K",
    d: "T",
    e: "E",
    f: "F",
    g: "G",
    h: "",
    i: "I",
    j: "J",
    k: "K",
    l: "L",
    m: "M",
    n: "N",
    o: "O",
    p: "P",
    q: "K",
    r: "R",
    s: "S",
    t: "T",
    u: "U",
    v: mode === "secondary" ? "W" : "V",
    w: mode === "secondary" ? "W" : "V",
    x: "KS",
    y: "I",
    z: mode === "secondary" ? "S" : "J",
  };

  return value
    .split("")
    .map((char) => map[char] ?? char.toUpperCase())
    .join("");
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ");
}

function isServiceOccupationTypeLabel(value) {
  return /\bservice\b/i.test(String(value || "").trim());
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const dob = new Date(dateValue);
  if (Number.isNaN(dob.getTime())) return null;
  return Math.floor((new Date() - dob) / (365.25 * 86400000));
}

function getLookupName(list, id) {
  if (!id) return "";
  const item = safeArray(list).find((x) => Number(x.id) === Number(id));
  return item?.name || item?.display_label || "";
}

function formatDropdownLabel(value) {
  return String(value || "")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function findConfigIdByLabel(list, label) {
  const normalizedLabel = String(label || "").trim().toLowerCase();
  if (!normalizedLabel) return "";
  const match = safeArray(list).find((item) => String(item?.name || item?.display_label || "").trim().toLowerCase() === normalizedLabel);
  return match?.id ? String(match.id) : "";
}

function occupationHasContent(occupation) {
  return Boolean(
    occupation?.occupation_type_id ||
    occupation?.occupation_type ||
    occupation?.company_name ||
    occupation?.occupation_details ||
    occupation?.work_profile ||
    occupation?.annual_income_amount ||
    occupation?.annual_turnover_amount
  );
}

function normalizeOccupationPayloadRow(occupation, occupationTypes, index) {
  const occupation_type =
    getLookupName(occupationTypes, occupation.occupation_type_id) ||
    occupation.occupation_type ||
    null;
  const isServiceOccupation = isServiceOccupationTypeLabel(occupation_type);

  return {
    ...occupation,
    occupation_type_id: occupation.occupation_type_id || null,
    occupation_type,
    company_name: occupation.company_name || null,
    occupation_details: occupation.occupation_details || null,
    work_profile: occupation.work_profile || null,
    annual_income_amount: isServiceOccupation ? occupation.annual_income_amount || null : null,
    annual_income_currency_code: isServiceOccupation
      ? normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, occupation.annual_income_amount)
      : null,
    annual_turnover_amount: isServiceOccupation ? null : occupation.annual_turnover_amount || null,
    annual_turnover_currency_code: isServiceOccupation
      ? null
      : normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, occupation.annual_turnover_amount),
    is_primary: index === 0,
    sort_order: occupation.sort_order || index + 1,
    status: occupation.status || "active",
  };
}

function withResolvedRelativeRelationTypeIds(form, relationTypes) {
  return {
    ...form,
    relatives: safeArray(form.relatives).map((relative) => ({
      ...relative,
      relation_type_id:
        relative.relation_type_id ||
        findConfigIdByLabel(relationTypes, relative.relation_type),
    })),
  };
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "0 KB";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function buildDocumentUploadSummary(uploadedFiles) {
  const processedPdfFiles = safeArray(uploadedFiles).filter(
    (file) =>
      file?.mime_type === "application/pdf" &&
      Number(file?.original_size_bytes || 0) > MAX_UPLOAD_FILE_SIZE_BYTES
  );
  if (!processedPdfFiles.length) return "";

  const compressedCount = processedPdfFiles.filter((file) => Boolean(file?.compressed)).length;
  const totalOriginalBytes = processedPdfFiles.reduce(
    (sum, file) => sum + Number(file?.original_size_bytes || 0),
    0
  );
  const totalStoredBytes = processedPdfFiles.reduce(
    (sum, file) => sum + Number((file?.stored_size_bytes ?? file?.file_size_bytes) || 0),
    0
  );

  return `Backend PDF optimization processed ${processedPdfFiles.length} file${processedPdfFiles.length === 1 ? "" : "s"} above ${MAX_UPLOAD_FILE_SIZE_LABEL}. Reduced ${compressedCount} file${compressedCount === 1 ? "" : "s"}, total ${formatFileSize(totalOriginalBytes)} to ${formatFileSize(totalStoredBytes)}.`;
}

function uniqueBy(items, key) {
  const seen = new Set();
  const out = [];
  safeArray(items).forEach((item) => {
    const value = item[key];
    if (value === null || value === undefined || value === "" || seen.has(String(value))) return;
    seen.add(String(value));
    out.push(item);
  });
  return out.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}

function safeNumberArray(value) {
  return safeArray(value).map((item) => Number(item)).filter(Number.isFinite);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function upsertReferralSource(items, source) {
  const nextId = Number(source?.id);
  if (!nextId) return safeArray(items);

  const nextItems = safeArray(items)
    .filter((item) => Number(item?.id) !== nextId)
    .concat({
      ...source,
      id: nextId,
      name: String(source?.name || "").trim(),
    });

  return nextItems.sort((a, b) => {
    const aSortOrder = Number.isFinite(Number(a?.sort_order)) ? Number(a.sort_order) : Number.POSITIVE_INFINITY;
    const bSortOrder = Number.isFinite(Number(b?.sort_order)) ? Number(b.sort_order) : Number.POSITIVE_INFINITY;
    if (aSortOrder !== bSortOrder) return aSortOrder - bSortOrder;
    const nameCompare = String(a?.name || "").localeCompare(String(b?.name || ""));
    if (nameCompare !== 0) return nameCompare;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

function hasDegreeCategoryMapping(items) {
  return safeArray(items).some((item) => Number(item?.education_category_id) > 0);
}

function buildDegreeNameOptions(items, currentValue = "", educationCategoryId = "") {
  const normalizedItems = safeArray(items);
  const categoryId = Number(educationCategoryId);
  const hasCategoryMapping = hasDegreeCategoryMapping(normalizedItems);
  const scopedItems =
    hasCategoryMapping && Number.isFinite(categoryId) && categoryId > 0
      ? normalizedItems.filter((item) => Number(item?.education_category_id) === categoryId)
      : normalizedItems;

  const baseItems = scopedItems
    .map((item) => ({
      value: String(item?.name || item?.display_label || "").trim(),
      label: String(item?.name || item?.display_label || "").trim(),
    }))
    .filter((item) => item.value);

  const current = String(currentValue || "").trim();
  if (current && !baseItems.some((item) => item.value === current)) {
    baseItems.unshift({ value: current, label: `${current} (Current)` });
  }

  return baseItems;
}

function isSingleMaritalStatusId(value, items) {
  const label = getLookupName(items, value);
  const normalized = String(label || "").trim().toLowerCase();
  return normalized === "single" || normalized === "never married";
}

function shouldShowChildrenSection(maritalStatusId, items) {
  if (!maritalStatusId) return false;
  return !isSingleMaritalStatusId(maritalStatusId, items);
}

function isIdentityField(field) {
  return [
    "first_name",
    "middle_name",
    "last_name",
    "gender_id",
    "gender",
    "birth_date",
    "dob",
    "birth_time",
  ].includes(field);
}

function applyLocationCascade(next, field, value, locations) {
  if (field === "country") {
    next.country = value;
    next.state_id = "";
    next.district_id = "";
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "state_id") {
    next.state_id = value;
    next.district_id = "";
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "district_id") {
    next.district_id = value;
    next.tehsil_id = "";
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "tehsil_id") {
    next.tehsil_id = value;
    next.city_id = "";
    next.current_location_id = "";
  }

  if (field === "city_id" || field === "current_location_id") {
    next.city_id = value;
    next.current_location_id = value;
    const city = locations.find((x) => Number(x.id) === Number(value));
    if (city) {
      next.country = city.country || "India";
      next.state_id = city.state_id || "";
      next.district_id = city.district_id || "";
      next.tehsil_id = city.tehsil_id || "";
    }
  }

  if (field === "birth_country") {
    next.birth_country = value;
    next.birth_state_id = "";
    next.birth_district_id = "";
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_state_id") {
    next.birth_state_id = value;
    next.birth_district_id = "";
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_district_id") {
    next.birth_district_id = value;
    next.birth_tehsil_id = "";
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_tehsil_id") {
    next.birth_tehsil_id = value;
    next.birth_place_city_id = "";
    next.birth_place_id = "";
  }

  if (field === "birth_place_city_id" || field === "birth_place_id") {
    next.birth_place_city_id = value;
    next.birth_place_id = value;
    const city = locations.find((x) => Number(x.id) === Number(value));
    if (city) {
      next.birth_country = city.country || "India";
      next.birth_state_id = city.state_id || "";
      next.birth_district_id = city.district_id || "";
      next.birth_tehsil_id = city.tehsil_id || "";
    }
  }
}

function applyRelativeLocationCascade(relative, field, value, locations) {
  if (field === "country") {
    relative.country = value;
    relative.state_id = "";
    relative.district_id = "";
    relative.tehsil_id = "";
    relative.city_id = "";
  }

  if (field === "state_id") {
    relative.state_id = value;
    relative.district_id = "";
    relative.tehsil_id = "";
    relative.city_id = "";
  }

  if (field === "district_id") {
    relative.district_id = value;
    relative.tehsil_id = "";
    relative.city_id = "";
  }

  if (field === "tehsil_id") {
    relative.tehsil_id = value;
    relative.city_id = "";
  }

  if (field === "city_id") {
    relative.city_id = value;
    const city = locations.find((x) => Number(x.id) === Number(value));
    if (city) {
      relative.country = city.country || "India";
      relative.state_id = city.state_id || "";
      relative.district_id = city.district_id || "";
      relative.tehsil_id = city.tehsil_id || "";
    }
  }
}

function applyAddressLocationCascade(address, field, value) {
  if (field === "country_id") {
    address.country_id = value;
    address.state_id = "";
    address.district_id = "";
    address.tehsil_id = "";
    address.city_id = "";
    address.village_id = "";
  }

  if (field === "state_id") {
    address.state_id = value;
    address.district_id = "";
    address.tehsil_id = "";
    address.city_id = "";
    address.village_id = "";
  }

  if (field === "district_id") {
    address.district_id = value;
    address.tehsil_id = "";
    address.city_id = "";
    address.village_id = "";
  }

  if (field === "tehsil_id") {
    address.tehsil_id = value;
    address.city_id = "";
    address.village_id = "";
  }

  if (field === "city_id") {
    address.city_id = value;
    address.village_id = "";
  }
}

function syncAddressCountryFromLocations(address, locations) {
  const rows = safeArray(locations);

  const match =
    rows.find((location) => Number(location.id) === Number(address.city_id)) ||
    rows.find((location) => Number(location.tehsil_id) === Number(address.tehsil_id)) ||
    rows.find((location) => Number(location.district_id) === Number(address.district_id)) ||
    rows.find((location) => Number(location.state_id) === Number(address.state_id));

  if (match?.country_id) {
    address.country_id = match.country_id;
  }
}

function getAddressCountryName(address, locations) {
  const selectedCountryId = Number(address?.country_id);
  if (!selectedCountryId) return "";

  const match = safeArray(locations).find(
    (location) => Number(location.country_id || location.countryId || location.countryID) === selectedCountryId
  );

  return String(match?.country || match?.country_name || match?.countryName || "").trim();
}

function shouldShowAddressVisaFields(address, locations) {
  const countryName = getAddressCountryName(address, locations);
  return Boolean(countryName) && countryName.toLowerCase() !== "india";
}

function syncAddressVisaFields(address, locations) {
  if (shouldShowAddressVisaFields(address, locations)) return;
  address.visa_status_id = "";
  address.visa_name_id = "";
}

const C = {
  bg: "#fff7f5",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  borderBold: "rgba(185,52,58,0.28)",
  borderFocus: "rgba(185,52,58,0.32)",
  borderSoft: "rgba(185,52,58,0.10)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#B9343A",
  blueLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  soft: "#fff7f5",
  error: "#D73C2E",
  green: "#B9343A",
  track: "#f4d9d6",
  shadow: "rgba(37,41,53,0.07)",
  shadowLight: "rgba(37,41,53,0.04)",
  shadowStrong: "rgba(37,41,53,0.10)",
  shadowAccent: "rgba(185,52,58,0.18)",
};

const S = {
  page: {
    minHeight: "100vh",
    padding: "28px 18px 44px",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 100%)`,
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
  },

  wrap: {
    maxWidth: 1120,
    margin: "0 auto",
    animation: "fadeUp 0.28s ease both",
  },

  hero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 20,
    padding: 28,
    borderRadius: 30,
    background: C.card,
    border: `1px solid ${C.border}`,
    boxShadow: `0 24px 70px ${C.shadow}`,
    marginBottom: 18,
  },

  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2.5,
    color: C.accent,
    fontWeight: 950,
    margin: "0 0 8px",
  },

  headerTitle: {
    margin: 0,
    color: C.text,
    fontSize: 36,
    fontWeight: 950,
    letterSpacing: -1,
  },

  headerSub: {
    margin: "8px 0 0",
    color: C.muted,
    lineHeight: 1.6,
    maxWidth: 720,
    fontSize: 14,
  },

  titleProgressTrack: {
    width: "min(520px, 100%)",
    height: 8,
    borderRadius: 999,
    background: C.track,
    overflow: "hidden",
    marginTop: 16,
  },

  titleProgressFill: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
  },

  titleProgressText: {
    marginTop: 7,
    color: C.muted,
    fontSize: 12,
    fontWeight: 850,
  },

  heroBadge: {
    whiteSpace: "nowrap",
    padding: "10px 14px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.blue,
    fontSize: 12,
    fontWeight: 950,
    border: `1px solid ${C.borderStrong}`,
  },

  workflowCard: {
    marginBottom: 18,
    padding: 20,
    borderRadius: 24,
    background: "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,245,242,0.98))",
    border: `1px solid ${C.borderStrong}`,
    boxShadow: `0 18px 40px rgba(185,52,58,0.08)`,
  },

  workflowHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 12,
  },

  workflowTitle: {
    color: C.text,
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: -0.3,
  },

  workflowSub: {
    marginTop: 6,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },

  workflowBadge: {
    padding: "9px 12px",
    borderRadius: 999,
    background: C.card,
    color: C.accent,
    border: `1px solid ${C.border}`,
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  workflowNotes: {
    marginBottom: 12,
    padding: "12px 14px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.88)",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
    lineHeight: 1.65,
  },

  workflowFileRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },

  workflowFileBtn: {
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 999,
    padding: "10px 14px",
    background: "rgba(255,255,255,0.95)",
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },

  workflowEmpty: {
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },

  stepper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 18,
    overflowX: "visible",
    paddingBottom: 2,
  },

  stepBtn: {
    flex: "0 1 auto",
    minWidth: 148,
    maxWidth: 220,
    padding: "12px 16px",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    background: C.card,
    cursor: "pointer",
    color: C.muted,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    fontWeight: 850,
    fontSize: 12,
  },

  stepActive: {
    background: C.text,
    color: "#fff",
    boxShadow: `0 14px 32px ${C.shadowAccent}`,
  },

  stepDone: {
    background: C.accentLight,
    color: C.accent,
    borderColor: C.borderStrong,
  },

  stepNo: {
    minWidth: 24,
    height: 24,
    padding: "0 7px",
    borderRadius: 999,
    display: "inline-grid",
    placeItems: "center",
    background: C.borderStrong,
    fontSize: 10,
    fontWeight: 950,
    lineHeight: 1,
  },

  stepLabel: {
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    lineHeight: 1.25,
    textAlign: "left",
  },

  panel: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 30,
    padding: 28,
    boxShadow: `0 22px 60px ${C.shadow}`,
    marginBottom: 16,
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px 22px",
  },

  locationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    alignItems: "center",
  },

  birthLocationStack: {
    display: "grid",
    gap: 14,
  },

  stepTitleRow: {
    marginBottom: 20,
  },

  sectionProgressTrack: {
    height: 6,
    borderRadius: 999,
    background: C.track,
    overflow: "hidden",
    marginTop: 8,
  },

  sectionProgressFill: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
  },

  label: {
    display: "block",
    fontSize: 11,
    color: C.text,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },

  input: {
    width: "100%",
    height: 46,
    padding: "0 14px",
    border: `1px solid ${C.borderFocus}`,
    borderRadius: 14,
    background: "#fff",
    color: C.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },
  searchableSelectInput: {
    minHeight: 46,
    padding: "0 14px",
    fontSize: 14,
    color: C.text,
  },
  searchableSelectChevronButton: {
    height: 46,
    color: C.muted,
  },
  searchableSelectMenu: {
    borderRadius: 16,
    border: `1px solid ${C.borderFocus}`,
    background: "#fff",
  },
  searchableSelectOption: {
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
  },
  searchableSelectOptionActive: {
    borderColor: C.accent,
    background: C.accentLight,
    color: C.accentDark,
  },

  inputErr: {
    borderColor: C.error,
    boxShadow: "0 0 0 3px rgba(220,38,38,0.08)",
  },

  prefRangeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  signedInputWrap: {
    position: "relative",
  },

  prefRangeInputRow: {
    display: "grid",
    gridTemplateColumns: "1fr 120px",
    gap: 12,
    alignItems: "stretch",
  },

  signedInputPrefix: {
    position: "absolute",
    top: "50%",
    left: 14,
    transform: "translateY(-50%)",
    color: C.accent,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
    pointerEvents: "none",
  },

  signedInput: {
    paddingLeft: 38,
  },

  autocompleteWrap: {
    position: "relative",
  },

  autocompleteMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 20,
    maxHeight: 240,
    overflowY: "auto",
    background: "#fff",
    border: `1px solid ${C.borderFocus}`,
    borderRadius: 14,
    boxShadow: "0 12px 32px rgba(37,41,53,0.14)",
    padding: 6,
  },

  autocompleteOption: {
    width: "100%",
    border: 0,
    background: "transparent",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: 10,
    color: C.text,
    cursor: "pointer",
  },

  autocompleteEmpty: {
    padding: "10px 12px",
    color: C.muted,
    fontSize: 13,
  },

  textarea: {
    minHeight: 96,
    paddingTop: 12,
    resize: "vertical",
  },

  errMsg: {
    display: "block",
    marginTop: 5,
    color: C.error,
    fontSize: 12,
    fontWeight: 800,
  },
  fieldHint: {
    marginTop: 6,
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.45,
  },

  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },

  sectionTitle: {
    margin: "0 0 14px",
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.8,
  },

  subCard: {
    position: "relative",
    padding: 18,
    marginBottom: 14,
    borderRadius: 22,
    background: C.card,
    border: `1px solid ${C.border}`,
    boxShadow: `0 10px 28px ${C.shadowLight}`,
  },

  miniCard: {
    position: "relative",
    padding: 14,
    marginTop: 10,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
  },

  relativeEduBox: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: `1px dashed ${C.borderBold}`,
  },

  addBtn: {
    padding: "8px 14px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.blueLight,
    color: C.blue,
    cursor: "pointer",
    fontWeight: 950,
  },

  removeBtn: {
    position: "absolute",
    top: 10,
    right: 12,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    background: C.accentLight,
    color: C.accent,
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 18,
  },

  infoBanner: {
    padding: 12,
    borderRadius: 16,
    background: C.blueLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.accent,
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 12,
  },

  emptyHint: {
    margin: "0 0 12px",
    color: C.muted,
    fontSize: 13,
    fontWeight: 700,
  },

  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },

  tagChip: {
    padding: "8px 14px",
    border: `1px solid ${C.borderBold}`,
    borderRadius: 999,
    background: "#fff",
    cursor: "pointer",
    color: C.muted,
    fontWeight: 850,
    fontSize: 12,
  },

  tagActive: {
    background: C.text,
    color: "#fff",
    borderColor: C.text,
  },

  dupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
    marginBottom: 20,
  },

  dupTitle: {
    margin: 0,
    fontSize: 24,
    color: C.text,
    fontWeight: 950,
  },

  dupSub: {
    margin: "8px 0 0",
    color: C.muted,
    lineHeight: 1.6,
    fontSize: 14,
    maxWidth: 700,
  },

  phoneticCard: {
    display: "none",
    minWidth: 170,
    padding: 14,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    textAlign: "center",
  },

  phoneticLabel: {
    display: "block",
    fontSize: 10,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    fontWeight: 900,
    marginBottom: 6,
  },

  dupActions: {
    display: "none",
    alignItems: "center",
    gap: 12,
    marginTop: 20,
    flexWrap: "wrap",
    padding: "0 22px 18px",
  },

  dupActionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    padding: "0 22px 18px",
  },

  deferChoiceRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    padding: "0 22px 18px",
  },

  deferChoiceBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontWeight: 900,
  },

  deferChoiceBtnActive: {
    background: C.accent,
    color: "#fff",
    borderColor: C.accent,
    boxShadow: `0 12px 30px ${C.shadowAccent}`,
  },

  deferInfoBox: {
    margin: "0 22px 18px",
    padding: 14,
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },

  checkBtn: {
    height: 44,
    padding: "0 20px",
    border: "none",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: `0 14px 30px ${C.shadowAccent}`,
  },

  safePill: {
    padding: "9px 12px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
  },

  dupResults: {
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  dupWarning: {
    padding: 14,
    borderRadius: 18,
    background: C.accentLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.accent,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 14,
  },

  dupTable: {
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    overflow: "hidden",
    background: "#fff",
  },

  dupRow: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1.2fr) 80px minmax(220px, 1fr)",
    gap: 12,
    padding: 14,
    borderBottom: `1px solid ${C.border}`,
    alignItems: "center",
  },

  dupHeadRow: {
    background: C.soft,
    color: C.muted,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },

  reasonList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },

  reasonChip: {
    padding: "5px 8px",
    borderRadius: 999,
    background: C.soft,
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
  },

  duplicateActionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
  },

  duplicateActionCard: {
    border: `1px solid ${C.borderBold}`,
    background: "#fff",
    borderRadius: 18,
    padding: 14,
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    color: C.text,
    minHeight: 96,
  },

  duplicateActionCardActive: {
    borderColor: C.accent,
    background: C.accentLight,
    boxShadow: `0 10px 24px ${C.shadowAccent}`,
  },

  overrideBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 14,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    color: C.text,
    fontSize: 14,
    fontWeight: 850,
  },

  finalResolveWrap: {
    marginTop: 26,
  },

  finalResolveNote: {
    marginBottom: 14,
    padding: 14,
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    color: C.muted,
    fontSize: 14,
    fontWeight: 800,
  },


  resolveShell: {
    marginTop: 22,
    borderRadius: 26,
    overflow: "hidden",
    border: `1px solid ${C.borderStrong}`,
    background: `linear-gradient(135deg, ${C.accentLight} 0%, #ffffff 42%, ${C.soft} 100%)`,
    boxShadow: `0 20px 54px ${C.shadow}`,
  },

  duplicateResolveOnly: {
    padding: "0 22px 22px",
  },

  resolveHero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    padding: 22,
    borderBottom: `1px solid ${C.borderStrong}`,
  },

  resolveEyebrow: {
    margin: "0 0 6px",
    color: C.accent,
    fontSize: 10,
    letterSpacing: 2.2,
    textTransform: "uppercase",
    fontWeight: 950,
  },

  resolveTitle: {
    margin: 0,
    color: C.text,
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: -0.5,
  },

  resolveSub: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.65,
    maxWidth: 720,
  },

  resolveScoreBubble: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    background: "#fff",
    border: `7px solid ${C.borderStrong}`,
    color: C.accentDark,
    boxShadow: `0 14px 34px ${C.shadowAccent}`,
    textAlign: "center",
  },

  resolveLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(360px, 0.9fr)",
    gap: 18,
    padding: 18,
  },

  matchList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  matchListHeader: {
    display: "flex",
    justifyContent: "space-between",
    color: C.muted,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    padding: "0 2px",
  },

  matchCard: {
    width: "100%",
    textAlign: "left",
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    borderRadius: 20,
    padding: 14,
    cursor: "pointer",
    color: C.text,
    boxShadow: `0 10px 26px ${C.shadowLight}`,
  },

  matchCardActive: {
    borderColor: C.accent,
    boxShadow: `0 0 0 4px ${C.borderSoft}, 0 16px 36px ${C.shadow}`,
  },

  matchCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },

  matchCardActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  matchName: {
    display: "block",
    fontSize: 15,
    color: C.text,
    marginBottom: 4,
  },

  matchMeta: {
    display: "block",
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  matchScore: {
    minWidth: 54,
    height: 34,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 13,
  },

  viewProfileBtn: {
    height: 34,
    padding: "0 14px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  matchLocation: {
    margin: "9px 0",
    color: C.muted,
    fontSize: 12,
    fontWeight: 750,
  },

  actionPanel: {
    background: "rgba(255,255,255,0.86)",
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 22,
    padding: 16,
    boxShadow: `0 14px 38px ${C.shadow}`,
  },

  actionPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },

  selectedTargetChip: {
    padding: "6px 10px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  neutralTargetChip: {
    padding: "6px 10px",
    borderRadius: 999,
    background: C.soft,
    color: C.muted,
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  actionCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    alignItems: "center",
  },

  actionBadge: {
    padding: "3px 7px",
    borderRadius: 999,
    background: "#fff",
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 950,
    border: `1px solid ${C.borderStrong}`,
  },

  flowPreview: {
    marginTop: 12,
    padding: 14,
    borderRadius: 18,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
  },

  flowTitle: {
    margin: "0 0 8px",
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1,
  },

  flowList: {
    margin: 0,
    paddingLeft: 18,
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.65,
    fontWeight: 750,
  },

  flowEmpty: {
    margin: 0,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  resolveError: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    background: C.accentLight,
    color: C.accent,
    fontSize: 12,
    fontWeight: 900,
  },


  spacer: {
    height: 28,
  },

  photoHint: {
    color: C.muted,
    fontSize: 13,
    marginTop: -6,
    marginBottom: 14,
  },

  mediaLead: {
    margin: "0 0 16px",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },

  mediaStack: {
    display: "grid",
    gap: 16,
  },

  multiDropdown: {
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    overflow: "hidden",
  },

  multiDropdownDisabled: {
    opacity: 0.7,
  },

  multiDropdownSummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "12px 14px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
  },

  multiDropdownSummaryText: {
    flex: 1,
    minWidth: 0,
    paddingTop: 1,
  },

  multiDropdownPlaceholder: {
    color: C.text,
  },

  multiDropdownSummaryDisabled: {
    cursor: "not-allowed",
  },

  multiDropdownCount: {
    minWidth: 24,
    height: 24,
    padding: "0 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },

  multiDropdownMenu: {
    display: "grid",
    gap: 10,
    padding: "0 14px 14px",
    borderTop: `1px solid ${C.borderSoft}`,
  },

  multiDropdownSelectedPanel: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },

  multiDropdownSelectedWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },

  multiDropdownSelectedChip: {
    maxWidth: "100%",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.accentLight,
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
    cursor: "pointer",
  },

  multiDropdownSelectedChipMuted: {
    cursor: "default",
    background: C.soft,
    color: C.muted,
  },

  multiDropdownSelectedChipLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 240,
  },

  multiDropdownSelectedChipRemove: {
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    lineHeight: 1,
    flexShrink: 0,
  },

  multiDropdownOptions: {
    display: "grid",
    gap: 10,
    maxHeight: 220,
    overflowY: "auto",
    paddingRight: 4,
  },

  multiDropdownEmpty: {
    paddingTop: 10,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  multiDropdownSearch: {
    marginTop: 12,
    minHeight: 40,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    padding: "0 12px",
    color: C.text,
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
  },

  cityTierFilterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
    marginBottom: 10,
  },

  cityTierFilterChip: {
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.text,
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  cityTierFilterChipActive: {
    background: C.accentLight,
    color: C.accent,
    borderColor: C.accent,
  },

  checkboxOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
  },

  checkboxOptionActive: {
    background: C.accentLight,
    borderColor: C.accent,
    boxShadow: `0 8px 18px ${C.borderSoft}`,
  },

  createOption: {
    width: "100%",
    textAlign: "left",
    borderStyle: "dashed",
    background: C.accentLight,
    color: C.accent,
  },

  createOptionIcon: {
    width: 18,
    height: 18,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    color: C.accent,
    fontWeight: 950,
  },

  checkboxInput: {
    width: 16,
    height: 16,
    accentColor: C.accent,
    margin: 0,
    flexShrink: 0,
  },

  dropZone: {
    border: `2px dashed ${C.borderBold}`,
    borderRadius: 24,
    padding: "30px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 7,
    cursor: "pointer",
    background: C.blueLight,
    marginBottom: 18,
  },

  dropIcon: {
    fontSize: 34,
    color: C.blue,
    fontWeight: 900,
  },

  dropText: {
    fontSize: 15,
    color: C.text,
    fontWeight: 950,
  },

  dropSub: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
  },
  processingDropZone: {
    opacity: 0.78,
    cursor: "progress",
  },
  processingInlineNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: C.accent,
    fontWeight: 900,
  },
  processingSpinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${C.border}`,
    borderTopColor: C.accent,
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },

  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 12,
  },

  previewCard: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 18,
    border: `2px solid ${C.borderStrong}`,
    background: C.soft,
    cursor: "pointer",
  },

  previewCardPrimary: {
    borderColor: C.accent,
    boxShadow: `0 0 0 4px ${C.borderSoft}`,
  },

  previewImg: {
    width: "100%",
    height: 145,
    objectFit: "cover",
    display: "block",
  },

  primaryBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    padding: "4px 9px",
    borderRadius: 999,
    background: C.accent,
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
  },

  previewRemove: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "none",
    background: "rgba(37,41,53,0.78)",
    color: "#fff",
    cursor: "pointer",
    fontSize: 17,
    lineHeight: "26px",
  },

  previewName: {
    padding: "7px 9px",
    fontSize: 11,
    color: C.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  docList: {
    display: "grid",
    gap: 10,
    marginTop: 14,
  },
  docItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    background: C.soft,
  },
  docName: {
    fontSize: 13,
    fontWeight: 900,
    color: C.text,
    wordBreak: "break-word",
  },
  docMeta: {
    fontSize: 11,
    color: C.muted,
    marginTop: 3,
    fontWeight: 700,
  },
  docRemove: {
    border: "none",
    borderRadius: 999,
    background: C.text,
    color: "#fff",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },

  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },

  navCounter: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 900,
  },

  navBtn: {
    height: 44,
    padding: "0 22px",
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 950,
  },

  navBack: {
    background: "#fff",
    color: C.muted,
    border: `1px solid ${C.borderStrong}`,
  },

  navNext: {
    background: C.text,
    color: "#fff",
  },

  navBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  navSubmit: {
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    boxShadow: `0 14px 30px ${C.shadowAccent}`,
  },

  errorBanner: {
    marginBottom: 14,
    padding: 14,
    borderRadius: 18,
    background: C.accentLight,
    color: C.accent,
    fontWeight: 900,
  },

  successWrap: {
    minHeight: "72vh",
    display: "grid",
    placeItems: "center",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 100%)`,
  },

  successCard: {
    width: "min(480px, 100%)",
    textAlign: "center",
    background: "#fff",
    padding: 42,
    borderRadius: 30,
    border: `1px solid ${C.border}`,
    boxShadow: `0 24px 70px ${C.shadowStrong}`,
  },

  successIcon: {
    width: 64,
    height: 64,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    background: C.accentLight,
    color: C.accent,
    fontSize: 32,
    margin: "0 auto 16px",
    fontWeight: 950,
  },

  successTitle: {
    margin: 0,
    color: C.text,
    fontSize: 28,
    fontWeight: 950,
  },

  successSub: {
    color: C.muted,
    lineHeight: 1.6,
    marginBottom: 22,
  },

  primaryBtn: {
    height: 44,
    padding: "0 20px",
    borderRadius: 999,
    border: "none",
    background: C.text,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },
};
